import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import ReactFlow, { 
  Background, 
  Controls, 
  MiniMap,
  useNodesState,
  useEdgesState,
  Handle,
  Position,
  MarkerType
} from 'reactflow';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Search,
  Plus,
  Grid3x3,
  Workflow,
  RefreshCw,
  Settings,
  Download,
  FileJson,
  FileText,
  Loader2,
  WifiOff,
  Clock,
  Heart,
  MapPin,
  GitBranch,
  Circle,
  Users,
  Map as MapIcon,
  Expand,
  Minimize2,
  ChevronDown,
  BookOpen,
  MoreHorizontal,
  UserPlus,
  ImagePlus,
  X,
  Mic,
  Headphones,
  PawPrint,
} from 'lucide-react';
import { Input } from '../components/ui/input';
import { useAuth } from '../contexts/AuthContext';
import { useOnboarding, OnboardingChecklist } from '../components/onboarding';
import { useOffline } from '../contexts/OfflineContext';
import SmartDateInput from '../components/SmartDateInput';
import LegacyPreservation from '../components/LegacyPreservation';
import FamilyInsights from '../components/FamilyInsights';
import InvestigationLeadsPanel from '../components/InvestigationLeadsPanel';
import DuplicateCandidatesPanel from '../components/DuplicateCandidatesPanel';
import RelationshipValidationPanel from '../components/RelationshipValidationPanel';
import ArchiveSearchPanel from '../components/ArchiveSearchPanel';
import CrossTreeMatchesPanel from '../components/CrossTreeMatchesPanel';
// Theme toggle removed - light mode only
import { humanizeError } from '../utils/errorHandling';
import { LoadingState } from '../components/ui/LoadingState';
import { PedigreeChart, FanChart, DescendantChart } from '../components/charts';
import { cn } from '@/lib/utils';
import {
  PersonAvatarWithIndicators,
  PersonSelectRow,
  PersonArchiveActivityRow,
} from '../components/PersonAvatarWithIndicators';
import { fetchPersonActivityMaps } from '../utils/personActivityCounts';
import { DEV_BACKEND_BASE_URL } from '../config/devBackendUrl';
import { resolveAssetUrl } from '../utils/resolveAssetUrl';
import { TREE_OPEN_ADD_PERSON_QUERY, setLastOpenedTreeId } from '../utils/archiveNavigation';
import { ADD_CONNECTION, linkOutcomeMessage } from '../utils/connectionCopy';
import { LEGACY_BOOK_COPY } from '../utils/humanCopy';
import { generatePlaywrightLegacyBookPdf } from '../utils/legacyBookPlaywrightExport';
import { ArchiveConnectionPrivacyNotice } from '../components/archive/ArchiveConnectionPrivacyNotice';
import { TESTER_SIMPLIFIED_UI, CHART_LAYOUTS_COMING_SOON, CORE_JOURNEY_UI, LEGACY_BOOK_PLAYWRIGHT_EXPORT } from '../config/testerRelease';
import { CoreArchiveJourneyPanel } from '../components/archive/CoreArchiveJourneyPanel';
import { CoreLegacyBookReadiness } from '../components/archive/CoreLegacyBookReadiness';
import { CORE_EMPTY_STATES } from '../content/coreJourneyContent';
import { BrandWordmark } from '../components/brand/BrandWordmark';
import { ArchiveContextNav } from '../components/archive/ArchiveContextNav';
import { CompanionBadge } from '../components/archive/CompanionBadge';
import {
  PROFILE_TYPE,
  COMPANION_TYPE,
  PET_SPECIES,
  COMPANION_TYPE_OPTIONS,
  countArchiveMembers,
  filterPeopleByGroup,
  getDisplayName,
  isGenealogicalPerson,
  getBirthDateLabel,
  getDeathDateLabel,
  getBiographicalNotesLabel,
} from '../utils/companionProfiles';
import {
  PROFILE_INTENTION,
  setProfileIntention,
} from '../utils/profileIntention';
import { ProfileIntentionPicker } from '../components/archive/ProfileIntentionPicker';
import { personListDateLine, personListLocationLine } from '../utils/personDisplay';
import { formatDisplayDate, formatPersonLifespan } from '../utils/dateDisplay';
import { ArchiveTreeCover } from '../components/archive/ArchiveTreeCover';
import { AudiobookPlayer } from '../components/audiobook';
import { BookProgressPanel } from '../components/book';
import { ArchiveSettingsPanel } from '../components/archive/ArchiveSettingsPanel';
import { MemoryMarkersWorkspace } from '../components/archive/MemoryMarkersWorkspace';
import { getMarkerTypeIcon, getMarkerTypeStyle, MARKER_TYPE_LABELS } from '../utils/memoryMarkers';
import { ArchiveProfileHint } from '../components/archive/ArchiveProfileHint';
import {
  PROFILE_GROUP_FILTERS,
  treeFilterChipClass,
  treeLayoutBtnClass,
  treeOutlineToolClass,
  treeProfileTypePickClass,
  TREE_TAB_TRIGGER_CLASS,
} from '../utils/treeWorkspaceUi';
import { treeHref, personHref } from '../utils/prettyArchiveLinks';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { SoftUpgradePrompt } from '../components/billing/SoftUpgradePrompt';
import { useUsageLimits } from '../hooks/useUsageLimits';
import { shouldShowPeopleGrowthPrompt } from '../utils/usageLimits';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || DEV_BACKEND_BASE_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Family Tree (graph) data flow — separate from scroll/layout:
 * 1. GET {API}/trees/{treeId} — tree metadata + role
 * 2. GET {API}/individuals/{treeId} — list of people (names, ids)
 * 3. GET {API}/relationships/{treeId} — used by pedigree/fan/descendants, not required for Tree View layout
 * 4. For each person: GET {API}/individuals/detail/{id} — parents, children, partnerships for edges/levels
 * 5. layoutFamilyTree(detailedPeople) → nodes + edges for React Flow (client-side only)
 */
const FT_GRAPH_DEBUG =
  process.env.REACT_APP_DEBUG_FAMILY_TREE === 'true' ||
  (process.env.NODE_ENV === 'development' && process.env.REACT_APP_DEBUG_FAMILY_TREE !== 'false');

const RECENT_PLACES_STORAGE_KEY = 'gb_archive_recent_places';
const MAX_RECENT_PLACES = 14;

function readRecentPlacesFromStorage() {
  try {
    const raw = localStorage.getItem(RECENT_PLACES_STORAGE_KEY);
    const arr = JSON.parse(raw);
    if (!Array.isArray(arr)) return [];
    return arr
      .filter((x) => typeof x === 'string' && x.trim())
      .map((x) => x.trim())
      .filter((v, i, a) => a.indexOf(v) === i)
      .slice(0, MAX_RECENT_PLACES);
  } catch {
    return [];
  }
}

function rememberPlaceStrings(...parts) {
  try {
    const add = parts.map((s) => (s || '').trim()).filter(Boolean);
    if (add.length === 0) return;
    const merged = [...add, ...readRecentPlacesFromStorage()].filter((v, i, a) => a.indexOf(v) === i).slice(0, MAX_RECENT_PLACES);
    localStorage.setItem(RECENT_PLACES_STORAGE_KEY, JSON.stringify(merged));
  } catch {
    /* ignore */
  }
}

/** Locale-aware example only — never written to the person record. */
function birthOrDeathPlacePlaceholder() {
  try {
    const loc = typeof navigator !== 'undefined' ? navigator.language || 'en' : 'en';
    if (loc.startsWith('en-GB') || loc.startsWith('en-IE')) return 'e.g. Newport Pagnell, England';
    if (loc.startsWith('en-AU')) return 'e.g. Ballarat, Victoria, Australia';
    if (loc.startsWith('en-CA')) return 'e.g. Saskatoon, Saskatchewan, Canada';
    if (loc.startsWith('en')) return 'e.g. Cincinnati, Ohio, United States';
    return 'e.g. City, region, country';
  } catch {
    return 'e.g. City, region, country';
  }
}

function ftGraphLog(...args) {
  if (FT_GRAPH_DEBUG) console.log('[FamilyTree:graph]', ...args);
}

/** Inline layout + surface so the chart works even if CSS load order regresses; pairs with index.css */
const FAMILY_TREE_ROOT_STYLE = {
  height: '100svh',
  maxHeight: '100svh',
  minHeight: 0,
};

const FAMILY_TREE_PARCHMENT_SURFACE_STYLE = {
  backgroundColor: 'hsl(38 26% 95%)',
  backgroundImage:
    'linear-gradient(165deg, hsl(40 22% 97%) 0%, hsl(38 24% 94.5%) 42%, hsl(36 18% 93%) 100%)',
  boxShadow: 'inset 0 1px 0 hsl(43 30% 88% / 0.5)',
};

const FAMILY_TREE_CHART_TAB_STYLE = {
  flex: '1 1 0%',
  minHeight: 0,
  minWidth: 0,
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
};

const FAMILY_TREE_CANVAS_AREA_STYLE = {
  flex: '1 1 0%',
  minHeight: 0,
  minWidth: 0,
  position: 'relative',
  overflow: 'hidden',
};

/** More padding when the graph is small so it sits comfortably in the workspace (not hugging edges). */
function getFamilyTreeFitPadding(nodeCount, isMobile) {
  if (nodeCount <= 0) return isMobile ? 0.12 : 0.22;
  if (isMobile) {
    if (nodeCount <= 4) return 0.2;
    if (nodeCount <= 12) return 0.15;
    return 0.11;
  }
  if (nodeCount <= 5) return 0.44;
  if (nodeCount <= 12) return 0.34;
  if (nodeCount <= 30) return 0.24;
  return 0.18;
}

const CHART_TIPS_STORAGE_KEY = 'generational-bridge:chart-workspace-hints-collapsed';

/** Intentional empty space: subtle copy so the canvas reads as a workspace, not a bug */
function ChartWorkspaceHintPanel({ variant, canAddMembers, isMobile, collapsed, onCollapse, onExpand }) {
  if (collapsed) {
    return (
      <div className="absolute inset-x-0 bottom-3 z-[8] flex justify-center px-3 sm:bottom-5 sm:px-4">
        <button
          type="button"
          onClick={onExpand}
          className="rounded-full border border-[hsl(43_22%_86%_/_0.65)] bg-[hsl(40_24%_97%_/_0.88)] px-3 py-1.5 text-[10px] font-medium tracking-wide text-muted-foreground/90 shadow-[0_2px_12px_hsl(28_15%_20%_/_0.06)] backdrop-blur-[2px] transition hover:bg-[hsl(40_24%_97%)] hover:text-[hsl(32_16%_28%)]"
          data-testid="show-tree-tips-btn"
        >
          Tree tips
        </button>
      </div>
    );
  }

  const isTree = variant === 'tree';

  return (
    <div className="absolute inset-x-0 bottom-3 z-[8] flex justify-center px-3 sm:bottom-5 sm:px-4">
      <div
        className="family-tree-workspace-hint relative max-w-md rounded-lg border border-[hsl(43_22%_86%_/_0.65)] bg-[hsl(40_24%_97%_/_0.88)] px-3.5 py-2.5 text-center shadow-[0_2px_12px_hsl(28_15%_20%_/_0.06)] backdrop-blur-[2px] sm:max-w-lg sm:px-4 sm:py-3"
        role="note"
        aria-label="Family tree workspace tips"
      >
        <button
          type="button"
          onClick={onCollapse}
          className="absolute right-2 top-2 flex h-6 w-6 items-center justify-center rounded-full text-muted-foreground/55 transition hover:bg-[hsl(40_20%_94%)] hover:text-muted-foreground"
          aria-label="Hide tree tips"
          data-testid="hide-tree-tips-btn"
        >
          <X className="h-3.5 w-3.5" aria-hidden />
        </button>
        <p className="pr-5 font-serif text-[12px] text-[hsl(32_16%_28%_/_0.92)] tracking-wide sm:text-[13px]">
          {isTree ? 'Drag to explore your family tree' : 'Drag to explore the chart'}
        </p>
        <p className="mt-1.5 text-[10.5px] leading-snug text-muted-foreground/85 sm:text-[11px]">
          {isTree
            ? canAddMembers
              ? 'Open space gives your family tree room to grow. Add relatives from List View anytime.'
              : 'Pan and zoom to follow every branch of your archive.'
            : 'The workspace expands with your research. Pan freely to see the full picture.'}
        </p>
        {isTree ? (
          <p className="mt-2 text-[10px] text-muted-foreground/65">
            {isMobile ? 'Pinch to zoom' : 'Zoom with Ctrl + scroll (or trackpad pinch)'}
          </p>
        ) : null}
      </div>
    </div>
  );
}

// Global cache for persisting tree data across navigation
const treeCache = new Map();

// Export function to clear cache when data changes elsewhere
export const clearTreeCache = (treeId) => {
  if (treeId) {
    treeCache.delete(treeId);
  } else {
    treeCache.clear();
  }
};

// Hook to detect mobile viewport
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(false);
  
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  
  return isMobile;
};

// Custom node component for people - refined archival style
const PersonNode = ({ data }) => {
  return (
    <div 
      className="group relative min-w-[160px] max-w-[180px] cursor-pointer rounded-2xl border border-border/50 bg-card/95 p-4 shadow-sm backdrop-blur-sm transition-all duration-300 hover:border-primary/30 hover:shadow-lg"
      onClick={data.onProfileClick}
      data-testid="tree-person-node"
    >
      {data.canEditTree && data.onAddRelated && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className="absolute right-1 top-1 z-10 flex h-7 w-7 items-center justify-center rounded-full border border-border/40 bg-background/90 text-muted-foreground opacity-0 shadow-sm transition-opacity hover:bg-muted/80 group-hover:opacity-100"
              onClick={(e) => e.stopPropagation()}
              aria-label={ADD_CONNECTION}
              data-testid="tree-person-node-menu"
            >
              <MoreHorizontal className="h-4 w-4" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-52" onClick={(e) => e.stopPropagation()}>
            <DropdownMenuItem onSelect={() => data.onAddRelated('parent')}>
              <UserPlus className="mr-2 h-4 w-4" />
              Add parent above
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={() => data.onAddRelated('child')}>
              <UserPlus className="mr-2 h-4 w-4" />
              Add child below
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={() => data.onAddRelated('spouse')}>
              <Heart className="mr-2 h-4 w-4" />
              Add spouse / partner
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )}
      {/* Connection handle - top (for parents) */}
      <Handle 
        type="target" 
        position={Position.Top} 
        className="!w-2 !h-2 !bg-border !border-0 !-top-1 opacity-0 group-hover:opacity-100 transition-opacity" 
      />
      
      <div className="flex flex-col items-center">
        <div className="mb-2.5">
          <PersonAvatarWithIndicators
            photoUrl={data.photo}
            initials={data.initials}
            size="md"
            showActivityIndicators={false}
          />
        </div>
        
        {/* Name and lifespan */}
        <div className="text-center px-1">
          <p className="font-serif text-[13px] font-medium text-foreground/85 leading-tight line-clamp-2">{data.name}</p>
          {data.lifespan && (
            <p className="text-[10px] text-muted-foreground/60 mt-1 font-mono tracking-tight">{data.lifespan}</p>
          )}
          <PersonArchiveActivityRow
            storyCount={data.storyCount || 0}
            documentCount={data.documentCount || 0}
          />
        </div>
      </div>
      
      {/* Connection handle - bottom (for children) */}
      <Handle 
        type="source" 
        position={Position.Bottom} 
        className="!w-2 !h-2 !bg-border !border-0 !-bottom-1 opacity-0 group-hover:opacity-100 transition-opacity" 
      />
    </div>
  );
};

const nodeTypes = {
  person: PersonNode,
};

// Helper to generate data hash for change detection
const BLANK_NEW_PERSON = {
  profile_type: PROFILE_TYPE.PERSON,
  profile_intention: PROFILE_INTENTION.STANDARD,
  companion_type: null,
  species: null,
  breed: '',
  family_connection: '',
  attached_to_person_id: null,
  given_names: '',
  surname: '',
  sex: null,
  birth_date: '',
  birth_place: '',
  death_date: '',
  death_place: '',
  biographical_notes: '',
};

const generateDataHash = (people) => {
  if (!people || people.length === 0) return '';
  return people.map(p => `${p.id}-${p.updated_at || ''}`).sort().join('|');
};

const TreeViewEnhanced = () => {
  const { treeId: routeTreeKey, familySlug } = useParams();
  const routeTreeParam = routeTreeKey || familySlug;
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { user } = useAuth();
  const {
    needsFirstPersonHint,
    needsAddParentHint,
    markFirstPersonAdded,
    markSecondPersonAdded,
    isOnboardingFullyDone,
    treeChecklistCollapsed,
    toggleTreeChecklistCollapsed,
    dismissHint,
  } = useOnboarding();
  const { isOnline } = useOffline();
  const [searchParams, setSearchParams] = useSearchParams();
  const [tree, setTree] = useState(null);
  const treeCacheKey = useMemo(() => tree?.id || routeTreeParam, [tree?.id, routeTreeParam]);
  const [people, setPeople] = useState([]);
  const navigateToPersonById = useCallback(
    (id) => {
      if (!id) return;
      const p = people.find((x) => x.id === id);
      navigate(personHref(p || { id }));
    },
    [people, navigate]
  );
  const navigateToMarkerSearchResult = useCallback(
    (marker) => {
      if (!marker) return;
      setSearchOpen(false);
      if (marker.person_id) {
        navigateToPersonById(marker.person_id);
        return;
      }
      setActiveTab('memories');
    },
    [navigateToPersonById]
  );
  const navigateToTimelineEvent = useCallback(
    (event) => {
      if (event?.event_type === 'marker') {
        if (event.person_id) {
          const p = people.find((x) => x.id === event.person_id);
          navigate(personHref(p || { id: event.person_id }));
          return;
        }
        setActiveTab('memories');
        return;
      }
      if (!event?.person_id) return;
      const p = people.find((x) => x.id === event.person_id);
      const base = personHref(p || { id: event.person_id });
      if (event.event_type === 'memory' && event.story_id) {
        navigate(`${base}?view=stories&story=${event.story_id}`);
        return;
      }
      navigate(base);
    },
    [people, navigate]
  );
  const [documentCounts, setDocumentCounts] = useState({}); // { personId: count }
  const [storyCounts, setStoryCounts] = useState({}); // { personId: count }
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredPeople, setFilteredPeople] = useState([]);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [addStep, setAddStep] = useState(1);
  const { snapshot: usageSnapshot } = useUsageLimits({
    treeId: tree?.id,
    enabled: Boolean(tree?.id),
  });
  /** When set, new person is linked to this anchor after creation (tree context menu). */
  const [addPersonLinkContext, setAddPersonLinkContext] = useState(null);
  
  // Initialize tab from URL param or default to 'grid'
  const initialTab = searchParams.get('tab') || 'grid';
  const [activeTab, setActiveTab] = useState(initialTab);

  useEffect(() => {
    const t = searchParams.get('tab');
    if (t && ['grid', 'chart', 'timeline', 'memories', 'audiobook', 'readiness', 'settings'].includes(t)) {
      setActiveTab(t);
    }
  }, [searchParams, routeTreeParam]);
  const [treeGenerated, setTreeGenerated] = useState(false);
  const [generatingTree, setGeneratingTree] = useState(false);
  const [dataHash, setDataHash] = useState('');
  const [showMiniMap, setShowMiniMap] = useState(false); // Hidden by default, toggle on desktop
  const [chartTipsCollapsed, setChartTipsCollapsed] = useState(() => {
    try {
      return localStorage.getItem(CHART_TIPS_STORAGE_KEY) === '1';
    } catch {
      return false;
    }
  });
  /** Expands only the chart workspace (not browser fullscreen) — reliable across browsers. */
  const [workspaceExpanded, setWorkspaceExpanded] = useState(false);
  
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);

  const familyTreeFitPadding = useMemo(
    () => getFamilyTreeFitPadding(nodes.length, isMobile),
    [nodes.length, isMobile]
  );
  const [exportingPdf, setExportingPdf] = useState(false);
  const [exportingPdfV2, setExportingPdfV2] = useState(false);
  const [playwrightExportPhase, setPlaywrightExportPhase] = useState(null);
  const [exportingJson, setExportingJson] = useState(false);
  const [deletingArchive, setDeletingArchive] = useState(false);
  const [timelineData, setTimelineData] = useState(null);
  const [timelineLoading, setTimelineLoading] = useState(false);
  
  // Search state (global search modal)
  const [searchOpen, setSearchOpen] = useState(false);
  const [globalSearchQuery, setGlobalSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchChapterFilter, setSearchChapterFilter] = useState('');
  const [chaptersAvailable, setChaptersAvailable] = useState([]);
  
  // Discovery/Visibility state
  const [visibility, setVisibility] = useState('private');
  const [visibilityLoading, setVisibilityLoading] = useState(false);
  const [indexedSurnames, setIndexedSurnames] = useState(0);
  
  // Chart layout state
  const [chartLayout, setChartLayout] = useState('tree'); // 'tree', 'pedigree', 'fan', 'descendants'
  const [selectedPerson, setSelectedPerson] = useState(null); // For chart focus
  const [relationships, setRelationships] = useState([]); // For chart visualizations

  const isChartFocusMode = useMemo(
    () => workspaceExpanded && activeTab === 'chart' && people.length > 0,
    [workspaceExpanded, activeTab, people.length]
  );

  useEffect(() => {
    if (TESTER_SIMPLIFIED_UI && CHART_LAYOUTS_COMING_SOON.has(chartLayout)) {
      setChartLayout('tree');
    }
  }, [chartLayout]);

  const showArchiveHeroCover = activeTab === 'grid' || activeTab === 'timeline' || activeTab === 'memories';
  const isFamilyTreeTab = activeTab === 'chart';
  const isTimelineTab = activeTab === 'timeline';
  const isMemoriesTab = activeTab === 'memories';
  const isAudiobookTab = activeTab === 'audiobook';
  const isReadinessTab = activeTab === 'readiness';
  const isSettingsTab = activeTab === 'settings';

  const trySetChartLayout = useCallback((layout) => {
    if (TESTER_SIMPLIFIED_UI && CHART_LAYOUTS_COMING_SOON.has(layout)) {
      toast.info('Pedigree and fan charts are planned — Tree layout works beautifully for now.');
      return;
    }
    setChartLayout(layout);
  }, []);

  const collapseChartTips = useCallback(() => {
    setChartTipsCollapsed(true);
    try {
      localStorage.setItem(CHART_TIPS_STORAGE_KEY, '1');
    } catch {
      /* ignore */
    }
  }, []);

  const expandChartTips = useCallback(() => {
    setChartTipsCollapsed(false);
    try {
      localStorage.removeItem(CHART_TIPS_STORAGE_KEY);
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    if (activeTab !== 'chart') setWorkspaceExpanded(false);
  }, [activeTab]);

  useEffect(() => {
    if (!isChartFocusMode) return;
    const onKey = (e) => {
      if (e.key === 'Escape') setWorkspaceExpanded(false);
    };
    document.addEventListener('keydown', onKey);
    const prevBody = document.body.style.overflow;
    const prevHtml = document.documentElement.style.overflow;
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevBody;
      document.documentElement.style.overflow = prevHtml;
    };
  }, [isChartFocusMode]);
  
  // Track if we should auto-generate
  const shouldAutoGenerate = useRef(false);
  /** Set in ReactFlow onInit — used to fitView after async node generation */
  const reactFlowInstanceRef = useRef(null);

  const [profileGroupFilter, setProfileGroupFilter] = useState('all');
  const [newPerson, setNewPerson] = useState({ ...BLANK_NEW_PERSON });
  const creatingProfileType = addPersonLinkContext
    ? PROFILE_TYPE.PERSON
    : newPerson.profile_type || PROFILE_TYPE.PERSON;
  const usesTwoStepAddFlow =
    Boolean(addPersonLinkContext) || creatingProfileType === PROFILE_TYPE.PERSON;

  const [recentPlacesForPicker, setRecentPlacesForPicker] = useState(() => readRecentPlacesFromStorage());

  const blankNewPersonFields = useMemo(() => ({ ...BLANK_NEW_PERSON }), []);
  const archiveMemberCounts = useMemo(() => countArchiveMembers(people), [people]);

  const attachedPetCountByPersonId = useMemo(() => {
    const counts = {};
    for (const p of people) {
      if (p.profile_type === PROFILE_TYPE.PET && p.attached_to_person_id) {
        counts[p.attached_to_person_id] = (counts[p.attached_to_person_id] || 0) + 1;
      }
    }
    return counts;
  }, [people]);

  const genealogicalPeopleForAttachment = useMemo(
    () => people.filter(isGenealogicalPerson),
    [people]
  );

  /** Standalone add-person: never reuse relationship/link context from chart menus or a prior session. */
  const openNeutralAddPersonDialog = useCallback(() => {
    setAddPersonLinkContext(null);
    setAddStep(1);
    setNewPerson(blankNewPersonFields);
    setShowAddDialog(true);
  }, [blankNewPersonFields]);

  useEffect(() => {
    if (showAddDialog && addStep === 2) {
      setRecentPlacesForPicker(readRecentPlacesFromStorage());
    }
  }, [showAddDialog, addStep]);

  const fetchTreeData = useCallback(async () => {
    try {
      const urlTree = `${API}/trees/${routeTreeParam}`;
      const urlPeople = `${API}/individuals/${routeTreeParam}`;
      ftGraphLog('fetchTreeData: request URLs', {
        BACKEND_URL,
        apiPrefix: API,
        matchesDefaultDevBackend:
          BACKEND_URL.replace(/\/$/, '') === DEV_BACKEND_BASE_URL.replace(/\/$/, ''),
        GET_tree: urlTree,
        GET_individuals_list: urlPeople,
      });

      const [treeRes, peopleRes] = await Promise.all([
        axios.get(urlTree),
        axios.get(urlPeople),
      ]);

      ftGraphLog('fetchTreeData: responses', {
        tree: {
          status: treeRes.status,
          id: treeRes.data?.id,
          name: treeRes.data?.name,
          role: treeRes.data?.role,
        },
        individuals_list: {
          status: peopleRes.status,
          count: Array.isArray(peopleRes.data) ? peopleRes.data.length : 'not-array',
          sampleIds: Array.isArray(peopleRes.data) ? peopleRes.data.slice(0, 5).map((p) => p?.id) : [],
        },
      });

      setTree(treeRes.data);
      if (treeRes.data?.id) {
        setLastOpenedTreeId(treeRes.data.id);
      }
      setPeople(peopleRes.data);
      setFilteredPeople(peopleRes.data);
      
      try {
        const urlRels = `${API}/relationships/${routeTreeParam}`;
        const relsRes = await axios.get(urlRels);
        ftGraphLog('fetchTreeData: relationships', {
          GET: urlRels,
          status: relsRes.status,
          count: Array.isArray(relsRes.data) ? relsRes.data.length : 'not-array',
        });
        setRelationships(relsRes.data || []);
      } catch (relErr) {
        ftGraphLog('fetchTreeData: relationships failed (optional for Tree View)', relErr?.message || relErr);
        setRelationships([]);
      }
      
      try {
        const { storyCounts: sc, documentCounts: dc } = await fetchPersonActivityMaps(
          BACKEND_URL,
          treeRes.data.id,
          peopleRes.data
        );
        setStoryCounts(sc);
        setDocumentCounts(dc);
      } catch {
        setStoryCounts({});
        setDocumentCounts({});
      }
      
      if (treeRes.data.role === 'owner') {
        try {
          const visRes = await axios.get(`${API}/trees/${treeRes.data.id}/visibility`);
          setVisibility(visRes.data.visibility);
          setIndexedSurnames(visRes.data.indexed_surnames);
        } catch {
          // owner-only
        }
      }
    } catch (error) {
      toast.error(humanizeError(error));
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  }, [routeTreeParam, navigate]);

  // Restore cached tree data on mount
  useEffect(() => {
    const cachedData = treeCache.get(treeCacheKey);
    if (cachedData) {
      setNodes(cachedData.nodes);
      setEdges(cachedData.edges);
      setTreeGenerated(true);
      setDataHash(cachedData.dataHash);
    }
  }, [treeCacheKey, setNodes, setEdges, setTreeGenerated, setDataHash]);

  useEffect(() => {
    fetchTreeData();
  }, [fetchTreeData]);

  useEffect(() => {
    let base = filterPeopleByGroup(people, profileGroupFilter);
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      base = base.filter(
        (person) =>
          getDisplayName(person).toLowerCase().includes(q) ||
          person.birth_place?.toLowerCase().includes(q) ||
          person.family_connection?.toLowerCase().includes(q)
      );
    }
    setFilteredPeople(base);
  }, [searchQuery, people, profileGroupFilter]);

  // Check if data has changed and invalidate cache if needed
  useEffect(() => {
    if (people.length > 0) {
      const newHash = generateDataHash(people);
      const cachedData = treeCache.get(treeCacheKey);
      
      if (cachedData && cachedData.dataHash !== newHash) {
        // Data has changed, invalidate cache
        treeCache.delete(treeCacheKey);
        setTreeGenerated(false);
        setNodes([]);
        setEdges([]);
        shouldAutoGenerate.current = true;
      }
      setDataHash(newHash);
    }
  }, [people, treeCacheKey, setNodes, setEdges, setTreeGenerated, setDataHash]);

  // Keep flow-node activity badges in sync when story/document counts load after layout
  useEffect(() => {
    if (!treeGenerated || nodes.length === 0) return;
    setNodes((nds) =>
      nds.map((n) => {
        if (n.type !== 'person') return n;
        const id = n.id;
        return {
          ...n,
          data: {
            ...n.data,
            storyCount: storyCounts[id] ?? 0,
            documentCount: documentCounts[id] ?? 0,
          },
        };
      })
    );
  }, [storyCounts, documentCounts, treeGenerated, nodes.length, setNodes]);

  // Auto-generate tree when switching to chart tab if people exist and no tree yet
  useEffect(() => {
    if (activeTab === 'chart' && people.length > 0 && !treeGenerated && !generatingTree) {
      const timer = setTimeout(() => {
        generateFamilyTree();
      }, 100);
      return () => clearTimeout(timer);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: tab/count flags only; generateFamilyTree closes over latest people
  }, [activeTab, people.length, treeGenerated, generatingTree]);

  // Auto-select person when switching to pedigree/fan/descendants with exactly one person
  useEffect(() => {
    if (chartLayout !== 'tree' && people.length === 1 && !selectedPerson) {
      setSelectedPerson(people[0].id);
    }
  }, [chartLayout, people, selectedPerson]);

  const generateFamilyTree = async (showToast = true) => {
    const treePeople = people.filter(isGenealogicalPerson);
    if (treePeople.length === 0) return;
    
    setGeneratingTree(true);
    try {
      const sampleDetailUrl = `${API}/individuals/detail/${treePeople[0]?.id}`;
      ftGraphLog('generateFamilyTree: start', {
        peopleCount: treePeople.length,
        exampleDetailUrl: sampleDetailUrl,
        BACKEND_URL,
        apiPrefix: API,
        matchesDefaultDevBackend:
          BACKEND_URL.replace(/\/$/, '') === DEV_BACKEND_BASE_URL.replace(/\/$/, ''),
      });

      // Fetch detailed relationship data for each person
      const detailResults = await Promise.all(
        treePeople.map(async (person) => {
          const url = `${API}/individuals/detail/${person.id}`;
          try {
            const response = await axios.get(url, {
              timeout: 20000,
            });
            return { ok: true, url, data: response.data };
          } catch (err) {
            ftGraphLog('generateFamilyTree: detail request failed', {
              url,
              status: err.response?.status,
              message: err.message,
            });
            return {
              ok: false,
              url,
              data: { ...person, parents: [], children: [], partnerships: [] },
            };
          }
        })
      );

      const detailedPeople = detailResults.map((r) => r.data);
      const detailFailed = detailResults.filter((r) => !r.ok).length;

      const relationshipSummary = detailedPeople.map((p) => ({
        id: p.id,
        parents: p.parents?.length ?? 0,
        children: p.children?.length ?? 0,
        partnerships: p.partnerships?.length ?? 0,
      }));
      ftGraphLog('generateFamilyTree: detail batch complete', {
        detailRequests: detailResults.length,
        detailFailed,
        relationshipSummary,
      });

      const allowTreeEdits = tree?.role === 'owner' || tree?.role === 'editor';
      const { nodes: generatedNodes, edges: generatedEdges } = layoutFamilyTree(detailedPeople, allowTreeEdits);

      ftGraphLog('generateFamilyTree: layout → React Flow (before setState)', {
        nodeCount: generatedNodes.length,
        edgeCount: generatedEdges.length,
        nodeIdsSample: generatedNodes.slice(0, 12).map((n) => n.id),
        edgeIdsSample: generatedEdges.slice(0, 8).map((e) => e.id),
      });

      setNodes(generatedNodes);
      setEdges(generatedEdges);
      setTreeGenerated(true);
      
      // Cache the generated tree
      const newHash = generateDataHash(treePeople);
      treeCache.set(tree.id, {
        nodes: generatedNodes,
        edges: generatedEdges,
        dataHash: newHash
      });
      setDataHash(newHash);
      
      if (showToast) {
        toast.success('Family tree generated!');
      }
    } catch (error) {
      console.error('generateFamilyTree', error);
      toast.error(humanizeError(error) || 'Unable to generate tree visualization');
    } finally {
      setGeneratingTree(false);
      shouldAutoGenerate.current = false;
    }
  };

  const layoutFamilyTree = (detailedPeople, allowTreeEdits = false) => {
    const nodeMap = new Map();
    const levelMap = new Map();
    const positionedNodes = [];
    const generatedEdges = [];

    // Calculate generations (levels). Copy visited per parent branch so diamonds work; cycles stop via visited.has.
    const calculateLevel = (personId, visited = new Set()) => {
      if (!personId || visited.has(personId)) return 0;
      visited.add(personId);

      const person = detailedPeople.find(p => p.id === personId);
      if (!person || !person.parents?.length) {
        return 0;
      }

      const parentLevels = person.parents
        .filter((parent) => parent?.id)
        .map((parent) => calculateLevel(parent.id, new Set(visited)));
      if (parentLevels.length === 0) return 0;
      return Math.max(...parentLevels) + 1;
    };

    // Assign levels to all people
    detailedPeople.forEach(person => {
      const level = calculateLevel(person.id);
      levelMap.set(person.id, level);
    });

    // Group by level
    const levelGroups = new Map();
    detailedPeople.forEach(person => {
      const level = levelMap.get(person.id);
      if (!levelGroups.has(level)) {
        levelGroups.set(level, []);
      }
      levelGroups.get(level).push(person);
    });

    // Position nodes
    const verticalSpacing = 200;
    const horizontalSpacing = 280;
    
    Array.from(levelGroups.keys()).sort((a, b) => a - b).forEach(level => {
      const peopleAtLevel = levelGroups.get(level);
      const levelWidth = peopleAtLevel.length * horizontalSpacing;
      const startX = -levelWidth / 2;

      peopleAtLevel.forEach((person, index) => {
        const x = startX + (index * horizontalSpacing) + horizontalSpacing / 2;
        const y = level * verticalSpacing;

        const initials = `${person.given_names?.charAt(0) || ''}${person.surname?.charAt(0) || ''}`;
        const lifespan = formatPersonLifespan(person) || '';

        const node = {
          id: person.id,
          type: 'person',
          position: { x, y },
          data: {
            name: `${person.given_names} ${person.surname}`,
            initials: initials,
            lifespan: lifespan,
            birthPlace: person.birth_place,
            photo: person.profile_image_url ? resolveAssetUrl(BACKEND_URL, person.profile_image_url) : null,
            documentCount: documentCounts[person.id] || 0,
            storyCount: storyCounts[person.id] || 0,
            onProfileClick: () => navigate(personHref(person)),
            canEditTree: allowTreeEdits,
            onAddRelated: allowTreeEdits
              ? (relation) => {
                  setAddPersonLinkContext({
                    anchorId: person.id,
                    anchorName: `${person.given_names || ''} ${person.surname || ''}`.trim(),
                    relation,
                  });
                  setAddStep(1);
                  setShowAddDialog(true);
                }
              : undefined,
          },
          draggable: false
        };

        positionedNodes.push(node);
        nodeMap.set(person.id, node);
      });
    });

    // Create edges for parent-child relationships
    detailedPeople.forEach(person => {
      if (person.parents && person.parents.length > 0) {
        person.parents.filter((p) => p?.id).forEach(parent => {
          generatedEdges.push({
            id: `${parent.id}-${person.id}`,
            source: parent.id,
            target: person.id,
            type: 'smoothstep',
            animated: false,
            style: { 
              stroke: 'hsl(var(--muted-foreground) / 0.3)', 
              strokeWidth: 1.5,
            },
            pathOptions: { borderRadius: 20 },
          });
        });
      }

      // Create edges for partnerships
      if (person.partnerships && person.partnerships.length > 0) {
        person.partnerships.filter((p) => p?.id).forEach(partner => {
          const edgeId1 = `partnership-${person.id}-${partner.id}`;
          const edgeId2 = `partnership-${partner.id}-${person.id}`;
          
          // Avoid duplicate partnership edges
          if (!generatedEdges.find(e => e.id === edgeId1 || e.id === edgeId2)) {
            generatedEdges.push({
              id: edgeId1,
              source: person.id,
              target: partner.id,
              type: 'straight',
              animated: false,
              style: { 
                stroke: 'hsl(var(--accent) / 0.4)', 
                strokeWidth: 1.5, 
                strokeDasharray: '4,4' 
              },
            });
          }
        });
      }
    });

    return { nodes: positionedNodes, edges: generatedEdges };
  };

  // Re-fit after nodes arrive — initial `fitView` prop can run before nodes exist
  useEffect(() => {
    if (!treeGenerated || nodes.length === 0 || chartLayout !== 'tree') return;
    const id = requestAnimationFrame(() => {
      reactFlowInstanceRef.current?.fitView?.({
        padding: familyTreeFitPadding,
        includeHiddenNodes: false,
      });
    });
    return () => cancelAnimationFrame(id);
  }, [treeGenerated, nodes.length, chartLayout, familyTreeFitPadding]);

  const handleAddPerson = async () => {
    if (tree?.role === 'viewer') {
      toast.error('You can only view this family tree');
      return;
    }

    const profileType = addPersonLinkContext ? PROFILE_TYPE.PERSON : newPerson.profile_type || PROFILE_TYPE.PERSON;

    if (!newPerson.given_names?.trim()) {
      toast.error('Please enter a name');
      return;
    }
    if (profileType === PROFILE_TYPE.PERSON && !newPerson.surname?.trim()) {
      toast.error('Please enter first name and last name');
      return;
    }
    if (profileType === PROFILE_TYPE.COMPANION && !newPerson.companion_type) {
      toast.error('Please choose how they connect to your family');
      return;
    }
    
    try {
      const cleanedPerson = {
        tree_id: tree.id,
        profile_type: profileType,
        given_names: newPerson.given_names.trim(),
        surname: (newPerson.surname || '').trim(),
        companion_type:
          profileType === PROFILE_TYPE.PET
            ? COMPANION_TYPE.FAMILY_PET
            : profileType === PROFILE_TYPE.COMPANION
              ? newPerson.companion_type
              : null,
        species: profileType === PROFILE_TYPE.PET ? newPerson.species || null : null,
        breed: profileType === PROFILE_TYPE.PET ? newPerson.breed?.trim() || null : null,
        family_connection:
          profileType === PROFILE_TYPE.COMPANION ? newPerson.family_connection?.trim() || null : null,
        attached_to_person_id:
          profileType === PROFILE_TYPE.PET || profileType === PROFILE_TYPE.COMPANION
            ? newPerson.attached_to_person_id || null
            : null,
        biographical_notes: newPerson.biographical_notes?.trim() || null,
        sex: profileType === PROFILE_TYPE.PERSON ? newPerson.sex || null : null,
        birth_date: newPerson.birth_date || null,
        birth_place: newPerson.birth_place || null,
        death_date: newPerson.death_date || null,
        death_place: newPerson.death_place || null,
      };
      if (profileType === PROFILE_TYPE.PERSON) {
        cleanedPerson.identity_status = 'private_placeholder';
      }

      const response = await axios.post(`${API}/individuals`, cleanedPerson);
      const newId = response.data?.id;
      if (newId && newPerson.profile_intention && newPerson.profile_intention !== PROFILE_INTENTION.STANDARD) {
        setProfileIntention(newId, newPerson.profile_intention);
      }
      const linkCtx = addPersonLinkContext;
      if (newId && linkCtx?.anchorId && linkCtx?.relation) {
        try {
          if (linkCtx.relation === 'parent') {
            await axios.post(`${API}/relationships/parent-child`, {
              tree_id: tree.id,
              parent_id: newId,
              child_id: linkCtx.anchorId,
              relationship_type: 'biological',
              notes: '',
            });
          } else if (linkCtx.relation === 'child') {
            await axios.post(`${API}/relationships/parent-child`, {
              tree_id: tree.id,
              parent_id: linkCtx.anchorId,
              child_id: newId,
              relationship_type: 'biological',
              notes: '',
            });
          } else if (linkCtx.relation === 'spouse') {
            await axios.post(`${API}/relationships/partnership`, {
              tree_id: tree.id,
              partner1_id: linkCtx.anchorId,
              partner2_id: newId,
              partnership_type: 'marriage',
              start_date: '',
              start_place: '',
              notes: '',
            });
          }
        } catch (relErr) {
          const detail = relErr?.response?.data?.detail;
          const detailStr = typeof detail === 'string' ? detail : '';
          let msg =
            humanizeError(relErr) || 'Person was added, but we could not create the family link automatically.';
          if (/already exists/i.test(detailStr)) {
            msg =
              'Person added. That family link already exists — open their profile to review connections.';
          } else if (detailStr) {
            msg = `Person added. Link could not be saved: ${detailStr}`;
          }
          toast.error(msg);
        }
      }
      setAddPersonLinkContext(null);
      toast.success(
        linkCtx?.relation
          ? linkOutcomeMessage(PROFILE_TYPE.PERSON, true, {
              identityStatus: response.data?.identity_status,
            })
          : linkOutcomeMessage(profileType, false, {
              identityStatus: response.data?.identity_status,
            })
      );
      const updatedPeople = [...people, response.data];
      setPeople(updatedPeople);
      rememberPlaceStrings(newPerson.birth_place, newPerson.death_place);
      setRecentPlacesForPicker(readRecentPlacesFromStorage());
      setShowAddDialog(false);
      setAddStep(1);
      setNewPerson(blankNewPersonFields);
      
      // Mark onboarding progress
      markFirstPersonAdded();
      if (updatedPeople.length >= 2) {
        markSecondPersonAdded();
      }
      
      // Invalidate cache and mark for regeneration
      treeCache.delete(tree.id);
      if (routeTreeParam && routeTreeParam !== tree.id) {
        treeCache.delete(routeTreeParam);
      }
      setTreeGenerated(false);
      setNodes([]);
      setEdges([]);

      if (response.data?.id) {
        navigate(personHref(response.data));
      }
    } catch (error) {
      toast.error(humanizeError(error));
    }
  };

  const getInitials = (person) => {
    return `${person.given_names?.charAt(0) || ''}${person.surname?.charAt(0) || ''}`;
  };

  // Export handlers
  const handleExportPdf = async (mode = 'keepsake') => {
    setExportingPdf(true);
    try {
      const params = mode === 'review' ? { mode: 'review' } : {};
      const response = await axios.get(`${API}/trees/${treeCacheKey}/export/pdf`, {
        responseType: 'blob',
        params,
      });
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const isPreview = response.headers?.['x-legacy-book-export'] === 'preview-watermarked';
      const suffix = mode === 'review' ? 'Review' : isPreview ? 'Preview' : 'Keepsake';
      link.download = `${tree?.name || 'Family_Tree'}_Legacy_Book_${suffix}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success(
        mode === 'review'
          ? 'Your review draft PDF has been downloaded.'
          : isPreview
            ? 'Your preview PDF has been downloaded.'
            : 'Your keepsake legacy book has been downloaded.'
      );
    } catch (error) {
      if (error?.response?.status === 402) {
        let detail = error.response?.data?.detail;
        if (error.response?.data instanceof Blob) {
          try {
            const parsed = JSON.parse(await error.response.data.text());
            detail = parsed?.detail ?? parsed;
          } catch {
            // ignore parse errors — fall back to generic copy
          }
        }
        const message = typeof detail === 'object' ? detail?.message : detail;
        toast.message(message || 'Full keepsake export is part of the Legacy Book founding offer.', {
          description: 'You can keep previewing your book in the app.',
          action: {
            label: 'View offer',
            onClick: () => navigate('/pricing#legacy-book'),
          },
        });
      } else {
        toast.error('Unable to generate PDF. Please try again.');
      }
    } finally {
      setExportingPdf(false);
    }
  };

  const handleExportPdfV2 = async () => {
    if (!treeCacheKey) return;
    setExportingPdfV2(true);
    setPlaywrightExportPhase('starting');
    try {
      await generatePlaywrightLegacyBookPdf(treeCacheKey, tree?.name, {
        onPhase: (phase) => setPlaywrightExportPhase(phase),
      });
      toast.success('Your experimental HTML PDF has been downloaded.');
    } catch (error) {
      toast.error(
        error?.message || 'Unable to generate experimental HTML PDF. Try the standard Legacy Book export.'
      );
    } finally {
      setExportingPdfV2(false);
      setPlaywrightExportPhase(null);
    }
  };

  const handleExportJson = async () => {
    setExportingJson(true);
    try {
      const response = await axios.get(`${API}/trees/${treeCacheKey}/export/json`);
      
      // Create download link
      const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const date = new Date().toISOString().split('T')[0].replace(/-/g, '');
      link.download = `${tree?.name || 'Family_Tree'}_backup_${date}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('Your data backup has been downloaded!');
    } catch (error) {
      toast.error('Unable to generate backup. Please try again.');
    } finally {
      setExportingJson(false);
    }
  };

  // Handle visibility toggle
  const handleVisibilityToggle = async (newVisibility) => {
    setVisibilityLoading(true);
    try {
      const response = await axios.patch(`${API}/trees/${treeCacheKey}/visibility`, {
        visibility: newVisibility
      });
      setVisibility(response.data.visibility);
      setIndexedSurnames(response.data.indexed_surnames);
      
      if (newVisibility === 'discoverable') {
        toast.success(`Tree is now discoverable. ${response.data.indexed_surnames} surname${response.data.indexed_surnames !== 1 ? 's' : ''} indexed.`);
      } else {
        toast.success('Tree is now private. All indexed data has been removed.');
      }
    } catch (error) {
      toast.error('Unable to update visibility settings');
    } finally {
      setVisibilityLoading(false);
    }
  };

  const handleDeleteArchive = async () => {
    if (!tree?.id || tree.role !== 'owner') return;
    const ok = window.confirm(
      `Delete "${tree.name}"?\n\nThis soft-deletes the archive and its people, stories, and documents. This cannot be undone from the app without support.`
    );
    if (!ok) return;
    setDeletingArchive(true);
    try {
      await axios.delete(`${API}/trees/${tree.id}`);
      clearTreeCache(tree.id);
      toast.success('Archive deleted.');
      navigate('/dashboard');
    } catch {
      toast.error('Unable to delete archive.');
    } finally {
      setDeletingArchive(false);
    }
  };

  const handleTreeSettingsUpdated = (patch) => {
    setTree((prev) => {
      if (!prev) return patch;
      return { ...prev, ...patch };
    });
    if (tree?.id) clearTreeCache(tree.id);
  };

  // Fetch timeline data when switching to timeline tab
  const fetchTimelineData = useCallback(async () => {
    setTimelineLoading(true);
    try {
      const response = await axios.get(`${API}/trees/${treeCacheKey}/timeline`);
      setTimelineData(response.data);
    } catch (error) {
      console.error('Failed to fetch timeline:', error);
      toast.error('Unable to load timeline');
    } finally {
      setTimelineLoading(false);
    }
  }, [treeCacheKey]);

  const handleMemoryMarkersChanged = useCallback(() => {
    setTimelineData(null);
    if (activeTab === 'timeline') {
      fetchTimelineData();
    }
  }, [activeTab, fetchTimelineData]);

  // Load timeline when tab becomes active
  useEffect(() => {
    if (activeTab === 'timeline') {
      fetchTimelineData();
    }
  }, [activeTab, fetchTimelineData]);

  // Search function with debounce
  const performSearch = useCallback(async (query, chapter = '') => {
    if (!isOnline) {
      // Offline search using cached people data
      const q = query.toLowerCase().trim();
      if (!q) {
        setSearchResults({ people: people, stories: [], total_results: people.length, query: '' });
        return;
      }
      const filteredResults = people.filter(p => {
        const fullName = `${p.given_names} ${p.surname}`.toLowerCase();
        const place = (p.birth_place || '').toLowerCase();
        return fullName.includes(q) || place.includes(q);
      }).map(p => ({
        id: p.id,
        name: `${p.given_names} ${p.surname}`,
        photo: p.profile_image_url,
        birth_date: p.birth_date,
        birth_place: p.birth_place,
        match_context: 'Offline search'
      }));
      setSearchResults({ people: filteredResults, stories: [], total_results: filteredResults.length, query });
      return;
    }

    setSearchLoading(true);
    try {
      const params = new URLSearchParams();
      if (query) params.append('q', query);
      if (chapter) params.append('chapter', chapter);
      
      const response = await axios.get(`${API}/trees/${treeCacheKey}/search?${params.toString()}`);
      setSearchResults(response.data);
      if (response.data.chapters_available) {
        setChaptersAvailable(response.data.chapters_available);
      }
    } catch (error) {
      console.error('Search failed:', error);
      toast.error('Search unavailable right now');
    } finally {
      setSearchLoading(false);
    }
  }, [treeCacheKey, isOnline, people]);

  // Debounced search effect
  useEffect(() => {
    if (!searchOpen) return;
    
    const timer = setTimeout(() => {
      performSearch(globalSearchQuery, searchChapterFilter);
    }, 300);
    
    return () => clearTimeout(timer);
  }, [globalSearchQuery, searchChapterFilter, searchOpen, performSearch]);

  // Reset search when modal closes
  const handleSearchClose = () => {
    setSearchOpen(false);
    setGlobalSearchQuery('');
    setSearchResults(null);
    setSearchChapterFilter('');
  };

  const canEditTree = useMemo(
    () => Boolean(tree && (tree.role === 'owner' || tree.role === 'editor')),
    [tree]
  );

  const canExportArchive = useMemo(() => {
    if (!tree) return false;
    if (tree.role === 'owner') return true;
    const p = tree.effective_permission;
    return p === 'export' || p === 'manage';
  }, [tree]);

  /** Deep links from person profile / overview: neutral add-person (no relationship link context). */
  useEffect(() => {
    if (loading || !tree) return;
    if (searchParams.get(TREE_OPEN_ADD_PERSON_QUERY) !== '1') return;
    if (!canEditTree) return;
    openNeutralAddPersonDialog();
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete(TREE_OPEN_ADD_PERSON_QUERY);
        return next;
      },
      { replace: true }
    );
  }, [loading, tree, canEditTree, searchParams, setSearchParams, openNeutralAddPersonDialog]);

  // Show loading state until both loading is complete AND tree data is available
  // This prevents the UI from rendering before we know the user's role
  if (loading || !tree) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingState message="Loading your family tree..." />
      </div>
    );
  }
  /** Onboarding strip + guidance only on Family Tree — List/Timeline stay clear for wayfinding. */
  const showTreeOnboardingChrome = activeTab === 'chart';
  const showTreeGuidance =
    showTreeOnboardingChrome &&
    canEditTree &&
    (needsFirstPersonHint || needsAddParentHint) &&
    !isChartFocusMode;

  return (
    <div
      className={cn(
        'tree-view-page flex min-h-0 flex-col overflow-x-hidden overflow-y-hidden bg-background transition-[opacity,transform] duration-200 ease-out',
        isChartFocusMode && 'fixed inset-0 z-[100]'
      )}
      style={FAMILY_TREE_ROOT_STYLE}
      data-tree-view-page
    >
      {!isChartFocusMode && (
        <nav className="flex-shrink-0 border-b border-border/40 bg-background">
          <div className="mx-auto max-w-7xl px-4 py-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <button
                  type="button"
                  onClick={() => navigate('/home')}
                  className="hidden text-lg transition-colors hover:opacity-85 sm:block"
                  data-testid="home-link"
                >
                  <BrandWordmark variant="treeNav" className="text-lg" />
                </button>
                <span className="hidden text-muted-foreground/40 sm:block">/</span>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => navigate('/dashboard')}
                  className="sm:hidden text-[hsl(32_10%_42%)] hover:bg-[hsl(42_26%_98%)] hover:text-[hsl(24_12%_22%)]"
                  data-testid="back-btn-mobile"
                >
                  <ArrowLeft className="h-5 w-5" />
                </Button>
                <div>
                  <h1 className="text-xl font-serif font-semibold" data-testid="tree-name">
                    {tree?.name}
                  </h1>
                  {!isSettingsTab ? (
                    <p className="text-sm text-muted-foreground">
                      {archiveMemberCounts.genealogical}{' '}
                      {archiveMemberCounts.genealogical === 1 ? 'person' : 'people'}
                      {archiveMemberCounts.archiveMembers > 0
                        ? ` · ${archiveMemberCounts.archiveMembers} profile${archiveMemberCounts.archiveMembers === 1 ? '' : 's'} beyond the tree`
                        : ''}{' '}
                      in your archive
                    </p>
                  ) : (
                    <p className="text-sm text-muted-foreground">Archive settings</p>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setSearchOpen(true)}
                  className="rounded-full text-[hsl(32_10%_42%)] hover:bg-[hsl(42_26%_98%)] hover:text-[hsl(24_12%_22%)]"
                  data-testid="global-search-btn"
                >
                  <Search className="h-5 w-5" />
                </Button>

                {activeTab === 'grid' && (
                  <div className="relative hidden sm:block">
                    <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      placeholder="Filter by name..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-48 pl-10"
                      data-testid="filter-input"
                    />
                  </div>
                )}
                {canEditTree && activeTab === 'grid' && (
                  <Button
                    className="rounded-full shadow-sm"
                    onClick={openNeutralAddPersonDialog}
                    data-testid="add-person-btn"
                  >
                    <Plus className="mr-2 h-5 w-5" />
                    <span className="hidden sm:inline">{ADD_CONNECTION}</span>
                    <span className="sm:hidden">Add</span>
                  </Button>
                )}
              </div>
            </div>
          </div>
        </nav>
      )}

      {isChartFocusMode && (
        <div
          className="flex flex-shrink-0 flex-col gap-3 border-b border-[hsl(38_14%_84%)] bg-[hsl(42_26%_99%)] px-4 py-3 backdrop-blur-md sm:flex-row sm:items-center sm:justify-between sm:gap-4 sm:px-6"
          data-testid="chart-focus-header"
        >
          <div className="flex min-w-0 flex-1 items-center gap-2 sm:gap-3">
            <Button
              type="button"
              variant="default"
              size="sm"
              className="shrink-0 rounded-full px-4 shadow-sm"
              onClick={() => setWorkspaceExpanded(false)}
              data-testid="exit-expanded-workspace"
            >
              <Minimize2 className="mr-2 h-4 w-4" aria-hidden />
              <span className="hidden sm:inline">Exit full view</span>
              <span className="sm:hidden">Exit</span>
            </Button>
            <span className="hidden min-w-0 truncate font-serif text-sm font-medium text-[hsl(24_14%_20%)] sm:block sm:text-base">
              {tree?.name}
            </span>
          </div>
          {canEditTree && (
            <Button
              size="sm"
              variant="outline"
              className={cn('shrink-0 rounded-full', treeOutlineToolClass(false))}
              onClick={openNeutralAddPersonDialog}
            >
              <Plus className="mr-2 h-4 w-4" />
              {ADD_CONNECTION}
            </Button>
          )}
          <p className="sr-only">Press Escape to exit full view</p>
        </div>
      )}

      {!isChartFocusMode && showTreeGuidance && (
        <div className="flex-shrink-0 border-b border-[hsl(38_16%_90%)] bg-[hsl(42_28%_99%)]">
          <div className="mx-auto max-w-7xl px-4 py-2.5 sm:px-6 lg:px-8">
            <div className="flex flex-col gap-3 rounded-xl border border-[hsl(38_14%_86%)] bg-[hsl(42_28%_99%)] px-4 py-3 shadow-[0_2px_20px_hsl(28_12%_18%_/_0.05)] sm:flex-row sm:items-center sm:justify-between sm:gap-5 sm:py-3.5 sm:pl-4 sm:pr-5">
              <div className="flex min-w-0 items-start gap-3 sm:items-center">
                <div
                  className="mt-0.5 flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl border border-[hsl(43_20%_86%)] bg-[hsl(42_26%_100%)] sm:mt-0 sm:h-10 sm:w-10"
                  aria-hidden
                >
                  <BookOpen className="h-[18px] w-[18px] text-[hsl(32_18%_40%)] sm:h-5 sm:w-5" />
                </div>
                <div className="min-w-0">
                  <p className="font-serif text-[15px] leading-snug text-[hsl(24_14%_16%)] sm:text-base">
                    {needsFirstPersonHint ? 'Begin your archive' : 'Connect the generations'}
                  </p>
                  <p className="mt-0.5 text-[12px] leading-snug text-[hsl(32_10%_42%)] sm:text-[13px]">
                    {needsFirstPersonHint
                      ? 'Add one family connection to begin, then open their profile to add more.'
                      : 'Add a family connection to your archive, then open their profile to link parents or children.'}
                  </p>
                </div>
              </div>
              <div className="flex shrink-0 items-center gap-2 sm:gap-2.5">
                <Button
                  size="default"
                  className="h-10 min-w-[9.5rem] rounded-full px-6 text-sm font-medium shadow-[0_2px_12px_hsl(28_25%_22%_/_0.12)]"
              onClick={openNeutralAddPersonDialog}
              data-testid="guidance-add-person-btn"
                >
                  <Plus className="mr-2 h-4 w-4" aria-hidden />
                  Add connection
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-9 text-[12px] text-[hsl(32_10%_44%)] hover:bg-[hsl(42_26%_98%)] hover:text-[hsl(24_12%_22%)]"
                  onClick={() => dismissHint(needsFirstPersonHint ? 'add-person' : 'add-parent')}
                  data-testid="guidance-dismiss"
                >
                  Not now
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div
        className={cn(
          'flex min-h-0 min-w-0 flex-1 overflow-hidden',
          !isChartFocusMode && 'lg:flex-row'
        )}
      >
        {!isChartFocusMode && (
          <ArchiveContextNav
            treeId={treeCacheKey}
            treeLinkBase={tree ? treeHref(tree) : undefined}
            treeName={tree?.name}
            variant="tree"
            canExport={people.length > 0}
            peopleCount={people.length}
            onExportPdf={handleExportPdf}
            onExportJson={handleExportJson}
            exportingPdf={exportingPdf}
            exportingJson={exportingJson}
          />
        )}
        <div
          className={cn(
            'flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden',
            !isChartFocusMode && 'pb-[calc(5.25rem+env(safe-area-inset-bottom,0px))] lg:pb-0'
          )}
        >
        {!isChartFocusMode && showTreeOnboardingChrome && (
          <div
            className={cn(
              'mx-auto w-full max-w-7xl flex-shrink-0 px-4 sm:px-6 lg:px-8',
              'py-2',
              treeChecklistCollapsed && !isOnboardingFullyDone && tree && 'pb-2'
            )}
          >
            {!isOnboardingFullyDone && tree && !treeChecklistCollapsed && (
              <OnboardingChecklist user={user} trees={[{ id: tree.id, name: tree.name, role: tree.role }]} />
            )}
            {!isOnboardingFullyDone && tree && treeChecklistCollapsed && (
              <button
                type="button"
                onClick={() => toggleTreeChecklistCollapsed()}
                className="flex w-full items-center justify-between gap-3 rounded-2xl border border-[hsl(38_16%_86%)] bg-[hsl(42_26%_99%)] px-4 py-3 text-left shadow-sm transition-colors hover:border-[hsl(38_14%_78%)] hover:bg-[hsl(42_24%_98%)]"
                data-testid="onboarding-checklist-restore"
              >
                <span className="font-serif text-[15px] text-[hsl(24_14%_18%)]">Your setup guide</span>
                <span className="flex items-center gap-2 text-[13px] text-[hsl(32_12%_42%)]">
                  Show steps
                  <ChevronDown className="h-4 w-4 opacity-70" aria-hidden />
                </span>
              </button>
            )}
          </div>
        )}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
          <div
            className={cn(
              'tree-main-scroll flex min-h-0 min-w-0 flex-1 flex-col',
              isFamilyTreeTab
                ? 'overflow-hidden'
                : isTimelineTab || isMemoriesTab || isAudiobookTab || isReadinessTab || isSettingsTab
                  ? 'overflow-auto'
                  : 'overflow-y-auto overflow-x-hidden'
            )}
            data-testid="tree-main-scroll"
          >
          {!isChartFocusMode && tree && showArchiveHeroCover && (
            <div className="mx-auto w-full max-w-7xl flex-shrink-0 px-4 pb-2 pt-0 sm:px-6 lg:px-8">
              <ArchiveTreeCover
                coverImageUrl={tree.cover_image_url}
                coverDisplay={tree.cover_display}
                alt={`${tree.name} family archive`}
                treeName={tree.name}
                variant="hero"
                className="rounded-2xl overflow-hidden"
              />
            </div>
          )}
          {!isChartFocusMode && isReadinessTab && (
            <div className="sticky top-0 z-20 flex-shrink-0 border-b bg-background/95 backdrop-blur-sm supports-[backdrop-filter]:bg-background/90">
              <div className="mx-auto max-w-7xl px-4 py-3 sm:px-6 lg:px-8">
                <p className="text-[10px] font-medium uppercase tracking-[0.16em] text-[hsl(32_10%_42%)]">
                  Preview &amp; export
                </p>
                <h1 className="font-serif text-lg font-normal text-[hsl(24_14%_16%)] sm:text-xl">
                  {CORE_JOURNEY_UI ? 'Legacy Book' : 'Keepsake readiness'}
                </h1>
                <p className="mt-0.5 text-sm text-muted-foreground">
                  {CORE_JOURNEY_UI
                    ? 'See how your archive is growing into something your family can keep.'
                    : 'Book progress, chapters, and export status for this tree.'}
                </p>
              </div>
            </div>
          )}
          {!isChartFocusMode && !isReadinessTab && (
            <div
              className={cn(
                'sticky top-0 z-20 flex-shrink-0 border-b bg-background/95 backdrop-blur-sm supports-[backdrop-filter]:bg-background/90',
                isSettingsTab && 'shadow-sm'
              )}
            >
              <div
                className={cn(
                  'mx-auto flex max-w-7xl items-center justify-between gap-3 px-4 py-2 sm:px-6 lg:px-8 sm:py-2.5',
                  isSettingsTab && 'shadow-sm'
                )}
              >
                <TabsList className="w-full sm:w-auto">
                <TabsTrigger value="grid" className={TREE_TAB_TRIGGER_CLASS} data-testid="grid-view-tab">
                  <Grid3x3 className="h-4 w-4 sm:mr-2" />
                  <span className="hidden sm:inline">{CORE_JOURNEY_UI ? 'People' : 'List View'}</span>
                  <span className="sm:hidden">{CORE_JOURNEY_UI ? 'People' : 'List'}</span>
                </TabsTrigger>
                {!CORE_JOURNEY_UI && (
                <>
                <TabsTrigger value="chart" className={TREE_TAB_TRIGGER_CLASS} data-testid="chart-view-tab">
                  <Workflow className="h-4 w-4 sm:mr-2" />
                  <span className="hidden sm:inline">Family Tree</span>
                  <span className="sm:hidden">Tree</span>
                </TabsTrigger>
                <TabsTrigger value="timeline" className={TREE_TAB_TRIGGER_CLASS} data-testid="timeline-tab">
                  <Clock className="h-4 w-4 sm:mr-2" />
                  <span className="hidden sm:inline">Timeline</span>
                  <span className="sm:hidden">Time</span>
                </TabsTrigger>
                <TabsTrigger value="memories" className={TREE_TAB_TRIGGER_CLASS} data-testid="memories-tab">
                  <MapPin className="h-4 w-4 sm:mr-2" />
                  <span className="hidden sm:inline">Places &amp; things</span>
                  <span className="sm:hidden">Memories</span>
                </TabsTrigger>
                <TabsTrigger value="audiobook" className={TREE_TAB_TRIGGER_CLASS} data-testid="audiobook-tab">
                  <Headphones className="h-4 w-4 sm:mr-2" />
                  <span className="hidden sm:inline">Audiobook</span>
                  <span className="sm:hidden">Listen</span>
                </TabsTrigger>
                </>
                )}
                {CORE_JOURNEY_UI && (
                <TabsTrigger value="readiness" className={TREE_TAB_TRIGGER_CLASS} data-testid="legacy-book-tab">
                  <BookOpen className="h-4 w-4 sm:mr-2" />
                  <span className="hidden sm:inline">Legacy Book</span>
                  <span className="sm:hidden">Book</span>
                </TabsTrigger>
                )}
                <TabsTrigger value="settings" className={TREE_TAB_TRIGGER_CLASS} data-testid="settings-tab">
                  <Settings className="h-4 w-4 sm:mr-2" />
                  <span className="hidden sm:inline">Archive settings</span>
                  <span className="sm:hidden">Settings</span>
                </TabsTrigger>
              </TabsList>
              <div className="flex shrink-0 items-center gap-2">
                {people.length > 0 && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="shrink-0 rounded-full"
                        disabled={exportingPdf || exportingJson}
                        data-testid="header-export-btn"
                      >
                        {exportingPdf || exportingJson ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin sm:mr-1.5" aria-hidden />
                        ) : (
                          <Download className="h-3.5 w-3.5 sm:mr-1.5" aria-hidden />
                        )}
                        <span className="hidden sm:inline">Export</span>
                        <span className="sr-only sm:hidden">Export</span>
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-56">
                      <DropdownMenuItem
                        onSelect={() => handleExportPdf('keepsake')}
                        disabled={exportingPdf}
                        data-testid="header-export-pdf"
                      >
                        <FileText className="mr-2 h-4 w-4" aria-hidden />
                        {LEGACY_BOOK_COPY.downloadKeepsake}
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onSelect={() => handleExportPdf('review')}
                        disabled={exportingPdf}
                        data-testid="header-export-pdf-review"
                      >
                        <FileText className="mr-2 h-4 w-4" aria-hidden />
                        {LEGACY_BOOK_COPY.reviewDraftPdf}
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onSelect={handleExportJson}
                        disabled={exportingJson}
                        data-testid="header-export-backup"
                      >
                        <FileJson className="mr-2 h-4 w-4" aria-hidden />
                        {LEGACY_BOOK_COPY.downloadBackup}
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
                {canEditTree && (
                  <Button
                    type="button"
                    size="sm"
                    className="shrink-0 whitespace-nowrap rounded-full"
                    onClick={openNeutralAddPersonDialog}
                    data-testid="header-add-connection-btn"
                  >
                    <Plus className="h-3.5 w-3.5 sm:mr-1.5" aria-hidden />
                    <span className="hidden md:inline">{ADD_CONNECTION}</span>
                    <span className="sr-only md:hidden">{ADD_CONNECTION}</span>
                  </Button>
                )}
              {canEditTree && showArchiveHeroCover ? (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="shrink-0 rounded-full border-[hsl(38_14%_82%)] bg-background px-2.5 text-[hsl(32_10%_38%)] hover:border-[hsl(38_14%_76%)] hover:bg-[hsl(42_26%_98%)] hover:text-[hsl(24_12%_22%)] sm:px-3"
                  onClick={() => setActiveTab('settings')}
                  title="Manage archive cover in Archive settings"
                  data-testid="manage-cover-btn"
                >
                  <ImagePlus className="h-3.5 w-3.5 sm:mr-1.5" aria-hidden />
                  <span className="hidden sm:inline">Manage cover</span>
                  <span className="sr-only sm:hidden">Manage cover</span>
                </Button>
              ) : null}
              </div>
              </div>
            </div>
          )}

          <TabsContent
            value="grid"
            className="mt-0 outline-none focus-visible:outline-none"
          >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 sm:py-6">
              {/* Two-column layout: people grid + preservation sidebar on large screens */}
              <div className="flex flex-col xl:flex-row gap-8 xl:gap-8">
                {/* Main content area */}
                <div className="flex-1 min-w-0">
                  {CORE_JOURNEY_UI && tree ? (
                    <CoreArchiveJourneyPanel treeId={tree.id} treeLinkBase={tree ? treeHref(tree) : undefined} />
                  ) : null}
                  {people.length > 0 ? (
                    <div className="mb-4 flex flex-wrap gap-2" data-testid="profile-group-filter">
                      {PROFILE_GROUP_FILTERS.map((opt) => (
                        <button
                          key={opt.id}
                          type="button"
                          onClick={() => setProfileGroupFilter(opt.id)}
                          className={treeFilterChipClass(profileGroupFilter === opt.id)}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  ) : null}
                  {people.length > 0 ? (
                    <ArchiveProfileHint
                      filterId={profileGroupFilter}
                      archiveMemberCounts={archiveMemberCounts}
                      onFilterChange={setProfileGroupFilter}
                    />
                  ) : null}
                  {filteredPeople.length === 0 && searchQuery ? (
                    <div className="text-center py-20" data-testid="no-results">
                      <Search className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
                      <h3 className="text-xl font-serif font-light mb-2">No matches found</h3>
                      <p className="text-muted-foreground">Try searching with a different name or place</p>
                    </div>
                  ) : filteredPeople.length === 0 && people.length > 0 ? (
                    <div className="text-center py-16" data-testid="empty-filter-state">
                      <Users className="h-14 w-14 text-muted-foreground/70 mx-auto mb-4" />
                      <h3 className="text-lg font-serif font-light mb-2">No connections in this view</h3>
                      <p className="text-sm text-muted-foreground mb-4">
                        Try another filter, or add a new family connection to your archive.
                      </p>
                      <Button variant="outline" size="sm" className={cn('rounded-full mr-2', treeOutlineToolClass(false))} onClick={() => setProfileGroupFilter('all')}>
                        Show all
                      </Button>
                      {canEditTree && (
                        <Button size="sm" className="rounded-full" onClick={openNeutralAddPersonDialog}>
                          <Plus className="mr-1.5 h-4 w-4" /> {ADD_CONNECTION}
                        </Button>
                      )}
                    </div>
                  ) : people.length === 0 ? (
                    <div className="text-center py-16" data-testid="empty-tree-state">
                      <Plus className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
                      <h3 className="text-xl font-serif font-light mb-2">
                        {CORE_JOURNEY_UI ? CORE_EMPTY_STATES.noPeople.title : 'Begin your archive'}
                      </h3>
                      <p className="text-muted-foreground mb-6 max-w-md mx-auto">
                        {CORE_JOURNEY_UI
                          ? CORE_EMPTY_STATES.noPeople.description
                          : 'Preserve the people, pets, and chosen family who shaped your story.'}
                      </p>
                      <Button onClick={openNeutralAddPersonDialog} size="lg" data-testid="empty-add-person-btn">
                        <Plus className="mr-2 h-5 w-5" />{' '}
                        {CORE_JOURNEY_UI ? CORE_EMPTY_STATES.noPeople.cta : ADD_CONNECTION}
                      </Button>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-2 gap-4 sm:gap-5 auto-rows-fr" data-testid="people-grid">
                      {filteredPeople.map((person) => {
                        const dateLine = personListDateLine(person);
                        const locationLine = personListLocationLine(person);
                        return (
                        <Card 
                          key={person.id} 
                          className="h-full border-border/50 bg-card/80 hover:border-border/70 hover:shadow-sm transition-[box-shadow,border-color] cursor-pointer group relative"
                          onClick={() => navigate(personHref(person))}
                          data-testid="person-card"
                        >
                          <div className="px-4 py-4 sm:px-5 sm:py-4">
                            <div className="flex items-start gap-3.5">
                              <PersonAvatarWithIndicators
                                photoUrl={
                                  person.profile_image_url
                                    ? resolveAssetUrl(BACKEND_URL, person.profile_image_url)
                                    : null
                                }
                                initials={getInitials(person)}
                                size="lg"
                                storyCount={storyCounts[person.id] || 0}
                                documentCount={documentCounts[person.id] || 0}
                              />
                              <div className="flex min-w-0 flex-1 flex-col justify-center gap-0.5">
                                <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                                  <h3 className="font-serif text-[15px] font-medium leading-snug text-foreground truncate" data-testid={`person-name-${person.id}`}>
                                    {getDisplayName(person)}
                                  </h3>
                                  <CompanionBadge person={person} />
                                </div>
                                {dateLine ? (
                                  <p className="text-xs leading-snug text-muted-foreground truncate">{dateLine}</p>
                                ) : null}
                                {isGenealogicalPerson(person) && attachedPetCountByPersonId[person.id] > 0 ? (
                                  <p className="inline-flex items-center gap-1 text-[10px] leading-snug text-[#5c4a32]/85">
                                    <PawPrint className="h-3 w-3 shrink-0 opacity-80" aria-hidden />
                                    {attachedPetCountByPersonId[person.id]} family pet
                                    {attachedPetCountByPersonId[person.id] > 1 ? 's' : ''}
                                  </p>
                                ) : null}
                                {locationLine ? (
                                  <p className="text-[11px] leading-snug text-muted-foreground/75 truncate">{locationLine}</p>
                                ) : null}
                              </div>
                            </div>
                          </div>
                        </Card>
                        );
                      })}
                    </div>
                  )}
                </div>
                
                {/* Legacy Preservation Sidebar - visible when tree has people */}
                {people.length > 0 && (
                  <div className="xl:w-[340px] xl:flex-shrink-0">
                    <div className="xl:sticky xl:top-24 space-y-3">
                      <LegacyPreservation treeId={treeCacheKey} />
                      <ArchiveSearchPanel treeId={treeCacheKey} treeName={tree?.name} allPeople={people} />
                      {!TESTER_SIMPLIFIED_UI && (
                        <>
                          <FamilyInsights treeId={treeCacheKey} treeSlug={tree?.slug} />
                          <InvestigationLeadsPanel treeId={treeCacheKey} treeSlug={tree?.slug} treeName={tree?.name} />
                          <CrossTreeMatchesPanel treeId={treeCacheKey} treeName={tree?.name} />
                          <RelationshipValidationPanel treeId={treeCacheKey} treeName={tree?.name} />
                          <DuplicateCandidatesPanel treeId={treeCacheKey} treeName={tree?.name} onRefresh={fetchTreeData} />
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </TabsContent>

          <TabsContent
            value="chart"
            className="family-tree-tab-root mt-0 flex min-h-0 min-w-0 w-full flex-1 flex-col overflow-hidden overscroll-none outline-none focus-visible:outline-none"
            style={FAMILY_TREE_CHART_TAB_STYLE}
            data-testid="chart-view"
            data-family-tree-tab-root=""
          >
            {people.length === 0 ? (
              <div className="flex flex-1 min-h-0 items-center justify-center">
                <div className="text-center px-4">
                  <Workflow className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-xl font-serif font-light mb-2">Add your first family connection</h3>
                  <p className="text-muted-foreground mb-6 max-w-md mx-auto">
                    Switch to List view and use <strong className="font-medium text-foreground">{ADD_CONNECTION}</strong>, or
                    open the form from here.
                  </p>
                  <Button
                    onClick={() => {
                      setActiveTab('grid');
                      openNeutralAddPersonDialog();
                    }}
                    data-testid="go-to-list-btn"
                  >
                    <Plus className="mr-2 h-4 w-4" /> {ADD_CONNECTION}
                  </Button>
                </div>
              </div>
            ) : (
              <>
                {/* Chart toolbar: grouped layout controls + workspace actions */}
                <div
                  className="flex-shrink-0 border-b border-[hsl(38_14%_88%)] bg-[hsl(42_26%_99%)]/98 backdrop-blur-md"
                  data-testid="chart-workspace-toolbar"
                >
                  <div className="mx-auto flex max-w-7xl flex-col gap-3 px-4 py-2.5 sm:px-6 sm:py-3 lg:flex-row lg:items-stretch lg:justify-between lg:gap-6">
                    <div className="flex min-w-0 flex-col gap-2.5 sm:flex-row sm:items-center sm:gap-5">
                      <div className="flex shrink-0 flex-col gap-1">
                        <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[hsl(32_9%_48%)]">
                          Layout
                        </span>
                        <div
                          className="inline-flex max-w-full flex-wrap gap-0.5 rounded-[14px] border border-[hsl(38_14%_86%)] bg-[hsl(42_28%_100%)] p-1 shadow-[inset_0_1px_3px_hsl(28_10%_18%_/_0.05)]"
                          role="group"
                          aria-label="Chart layout"
                        >
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => trySetChartLayout('tree')}
                            className={treeLayoutBtnClass(chartLayout === 'tree')}
                            data-testid="layout-tree-btn"
                          >
                            <GitBranch className="mr-1.5 h-3.5 w-3.5" aria-hidden />
                            Tree
                          </Button>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => trySetChartLayout('pedigree')}
                            className={treeLayoutBtnClass(chartLayout === 'pedigree')}
                            data-testid="layout-pedigree-btn"
                            disabled={TESTER_SIMPLIFIED_UI && CHART_LAYOUTS_COMING_SOON.has('pedigree')}
                          >
                            <Users className="mr-1.5 h-3.5 w-3.5" aria-hidden />
                            <span className="hidden sm:inline">Pedigree</span>
                            <span className="sm:hidden">Ped.</span>
                            {TESTER_SIMPLIFIED_UI && CHART_LAYOUTS_COMING_SOON.has('pedigree') ? (
                              <span className="ml-1 text-[10px] font-normal text-muted-foreground">Soon</span>
                            ) : null}
                          </Button>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => trySetChartLayout('fan')}
                            className={treeLayoutBtnClass(chartLayout === 'fan')}
                            data-testid="layout-fan-btn"
                            disabled={TESTER_SIMPLIFIED_UI && CHART_LAYOUTS_COMING_SOON.has('fan')}
                          >
                            <Circle className="mr-1.5 h-3.5 w-3.5" aria-hidden />
                            Fan
                            {TESTER_SIMPLIFIED_UI && CHART_LAYOUTS_COMING_SOON.has('fan') ? (
                              <span className="ml-1 text-[10px] font-normal text-muted-foreground">Soon</span>
                            ) : null}
                          </Button>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => trySetChartLayout('descendants')}
                            className={treeLayoutBtnClass(chartLayout === 'descendants')}
                            data-testid="layout-descendants-btn"
                          >
                            <Workflow className="mr-1.5 h-3.5 w-3.5" aria-hidden />
                            <span className="hidden sm:inline">Descendants</span>
                            <span className="sm:hidden">Desc.</span>
                          </Button>
                        </div>
                      </div>

                    {/* Person selector for pedigree/fan/descendants */}
                    {chartLayout !== 'tree' && (
                      <div className="flex min-w-0 max-w-full flex-1 items-center gap-2 sm:max-w-[min(100%,360px)]">
                        <span className="hidden shrink-0 text-[10px] font-semibold uppercase tracking-wide text-[hsl(32_10%_46%)] sm:inline">
                          Focus
                        </span>
                        <Select
                          value={selectedPerson || ''}
                          onValueChange={(v) => setSelectedPerson(v || null)}
                        >
                          <SelectTrigger
                            className="h-auto min-h-9 min-w-0 flex-1 gap-2 py-1.5 text-xs"
                            data-testid="person-select"
                          >
                            <div className="flex items-center gap-2 min-w-0 flex-1">
                              {selectedPerson && (() => {
                                const p = people.find((x) => x.id === selectedPerson);
                                if (!p) return null;
                                return (
                                  <PersonAvatarWithIndicators
                                    photoUrl={
                                      p.profile_image_url
                                        ? resolveAssetUrl(BACKEND_URL, p.profile_image_url)
                                        : null
                                    }
                                    initials={getInitials(p)}
                                    size="xs"
                                    storyCount={storyCounts[p.id] || 0}
                                    documentCount={documentCounts[p.id] || 0}
                                  />
                                );
                              })()}
                              <SelectValue placeholder="Select person..." className="truncate text-left" />
                            </div>
                          </SelectTrigger>
                          <SelectContent className="max-h-[min(60vh,320px)]">
                            {people.map((p) => (
                              <SelectItem
                                key={p.id}
                                value={p.id}
                                textValue={`${p.given_names || ''} ${p.surname || ''}`.trim() || 'Unknown'}
                                className="py-2 pl-2 pr-8"
                              >
                                <PersonSelectRow
                                  person={p}
                                  photoBaseUrl={BACKEND_URL}
                                  storyCount={storyCounts[p.id] || 0}
                                  documentCount={documentCounts[p.id] || 0}
                                />
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                    </div>

                    <div className="flex w-full flex-wrap items-center gap-2 border-t border-[hsl(38_14%_90%)] pt-3 sm:gap-2.5 lg:w-auto lg:border-t-0 lg:pt-0">
                      <span className="mr-auto w-full text-[10px] font-semibold uppercase tracking-[0.14em] text-[hsl(32_9%_48%)] lg:mr-0 lg:hidden lg:w-0">
                        View
                      </span>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className={cn('px-3.5', treeOutlineToolClass(isChartFocusMode))}
                        onClick={() =>
                          isChartFocusMode ? setWorkspaceExpanded(false) : setWorkspaceExpanded(true)
                        }
                        title={
                          isChartFocusMode
                            ? 'Return to the standard tree page layout'
                            : 'Use the full window for the chart. Press Esc to exit.'
                        }
                        data-testid="expand-workspace-btn"
                      >
                        {isChartFocusMode ? (
                          <>
                            <Minimize2 className="mr-1.5 h-3.5 w-3.5 shrink-0" aria-hidden />
                            <span className="hidden sm:inline">Exit full view</span>
                            <span className="sm:hidden">Exit</span>
                          </>
                        ) : (
                          <>
                            <Expand className="mr-1.5 h-3.5 w-3.5 shrink-0" aria-hidden />
                            <span className="hidden sm:inline">Full view</span>
                            <span className="sm:hidden">Full</span>
                          </>
                        )}
                      </Button>
                      {chartLayout === 'tree' && (
                        <>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className={treeOutlineToolClass(showMiniMap)}
                            onClick={() => setShowMiniMap(!showMiniMap)}
                            data-testid="toggle-minimap-btn"
                            title={
                              showMiniMap
                                ? 'Hide the small overview map'
                                : 'Show a small overview map of the tree'
                            }
                            aria-pressed={showMiniMap}
                          >
                            <MapIcon className="mr-1.5 h-3.5 w-3.5 shrink-0" aria-hidden />
                            <span className="hidden sm:inline">Overview</span>
                            <span className="sm:hidden">Map</span>
                          </Button>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className={treeOutlineToolClass(false)}
                            onClick={() => generateFamilyTree(true)}
                            disabled={generatingTree}
                            data-testid="refresh-tree-btn"
                            title="Rebuild the tree from the latest relationships"
                          >
                            <RefreshCw
                              className={`mr-1.5 h-3.5 w-3.5 shrink-0 ${generatingTree ? 'animate-spin' : ''}`}
                              aria-hidden
                            />
                            <span className="hidden sm:inline">Refresh</span>
                          </Button>
                        </>
                      )}
                      {canEditTree && (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className={treeOutlineToolClass(false)}
                          onClick={openNeutralAddPersonDialog}
                          data-testid="chart-add-connection-btn"
                        >
                          <UserPlus className="mr-1.5 h-3.5 w-3.5 shrink-0" aria-hidden />
                          <span className="hidden sm:inline">{ADD_CONNECTION}</span>
                          <span className="sm:hidden">Add</span>
                        </Button>
                      )}
                    </div>
                  </div>
                </div>

                {/* This div is the real flex-1 canvas region (the one DevTools highlights). Toolbar is a sibling above. */}
                <div
                  className="family-tree-canvas-area family-tree-workspace relative min-h-0 min-w-0 w-full flex-1"
                  style={FAMILY_TREE_CANVAS_AREA_STYLE}
                  data-family-tree-canvas-area=""
                >
                  {/* Tree View (existing) */}
                  {chartLayout === 'tree' && (
                    <>
                      {generatingTree ? (
                        <div
                          className="family-tree-canvas absolute inset-0 flex items-center justify-center"
                          style={FAMILY_TREE_PARCHMENT_SURFACE_STYLE}
                          data-family-tree-canvas="loading"
                        >
                          <div className="text-center">
                            <RefreshCw className="h-16 w-16 text-primary mx-auto mb-4 animate-spin" />
                            <h3 className="text-xl font-serif font-light mb-2">Generating Family Tree...</h3>
                            <p className="text-muted-foreground">Analyzing relationships and building visualization</p>
                          </div>
                        </div>
                      ) : treeGenerated && nodes.length > 0 ? (
                        <div
                          className="tree-react-flow-host family-tree-canvas absolute inset-0 min-h-0 min-w-0 h-full w-full"
                          style={FAMILY_TREE_PARCHMENT_SURFACE_STYLE}
                          data-family-tree-react-flow-host=""
                        >
                          <ReactFlow
                            className="family-tree-flow h-full w-full"
                            style={{ width: '100%', height: '100%', background: 'transparent' }}
                            onInit={(instance) => {
                              reactFlowInstanceRef.current = instance;
                            }}
                            nodes={nodes}
                            edges={edges}
                            onNodesChange={onNodesChange}
                            onEdgesChange={onEdgesChange}
                            nodeTypes={nodeTypes}
                            fitView
                            fitViewOptions={{ padding: familyTreeFitPadding, includeHiddenNodes: false }}
                            minZoom={0.1}
                            maxZoom={2}
                            nodesDraggable={false}
                            nodesConnectable={false}
                            elementsSelectable={true}
                            /* Wheel/trackpad scroll pans the canvas by default — feels like nodes “vanish”.
                               Zoom with scroll instead; pan by dragging empty space (or node drag). */
                            panOnScroll={false}
                            panOnDrag
                            zoomOnPinch={true}
                            zoomOnScroll={!isMobile}
                            zoomActivationKeyCode={!isMobile ? ['Control', 'Meta'] : undefined}
                            preventScrolling
                            proOptions={{ hideAttribution: true }}
                          >
                            <Background
                              color="hsl(43 16% 68% / 0.45)"
                              gap={22}
                              size={1}
                              variant="dots"
                            />
                            <Controls 
                              showInteractive={false}
                              showZoom={true}
                              showFitView={true}
                              position="bottom-left"
                              className="family-tree-controls !z-[30] !bg-[hsl(40_16%_99%_/_0.92)] !backdrop-blur-md !border !border-[hsl(43_22%_82%_/_0.9)] !rounded-lg !shadow-[0_4px_24px_hsl(20_10%_10%_/_0.08)]"
                            />
                            {showMiniMap && (
                              <MiniMap 
                                nodeColor="hsl(var(--primary) / 0.6)"
                                maskColor="hsl(40 18% 96% / 0.85)"
                                className="!z-[25] !bg-[hsl(40_16%_99%_/_0.92)] !backdrop-blur-md !border !border-[hsl(43_22%_82%_/_0.9)] !rounded-lg !shadow-[0_4px_20px_hsl(20_10%_10%_/_0.06)]"
                                style={{ 
                                  width: isMobile ? 100 : 120, 
                                  height: isMobile ? 70 : 80 
                                }}
                                pannable
                                zoomable
                              />
                            )}
                          </ReactFlow>
                        </div>
                      ) : (
                        <div
                          className="family-tree-canvas absolute inset-0 flex items-center justify-center"
                          style={FAMILY_TREE_PARCHMENT_SURFACE_STYLE}
                          data-family-tree-canvas="empty-cta"
                        >
                          <div className="text-center px-4">
                            <Workflow className="h-16 w-16 md:h-20 md:w-20 text-primary/30 mx-auto mb-4 md:mb-6" />
                            <h3 className="text-xl md:text-2xl font-serif font-light mb-2 md:mb-3">Ready to See Your Family Tree?</h3>
                            <p className="text-sm md:text-base text-muted-foreground mb-4 md:mb-6 max-w-md mx-auto">
                              Click the button below to automatically generate a visual diagram of your family connections
                            </p>
                            <Button 
                              size="lg" 
                              onClick={() => generateFamilyTree()}
                              disabled={generatingTree}
                              data-testid="generate-tree-btn"
                            >
                              <Workflow className="mr-2 h-5 w-5" />
                              Generate Family Tree
                            </Button>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                  
                  {/* Pedigree Chart */}
                  {chartLayout === 'pedigree' && (
                    <div
                      className="tree-react-flow-host family-tree-canvas absolute inset-0 min-h-0 min-w-0"
                      style={FAMILY_TREE_PARCHMENT_SURFACE_STYLE}
                      data-family-tree-react-flow-host="pedigree"
                    >
                      <PedigreeChart
                        individuals={people}
                        relationships={relationships}
                        rootPersonId={selectedPerson}
                        onPersonClick={(id) => navigateToPersonById(id)}
                      />
                    </div>
                  )}
                  
                  {/* Fan Chart */}
                  {chartLayout === 'fan' && (
                    <div
                      className="tree-react-flow-host family-tree-canvas absolute inset-0 min-h-0 min-w-0"
                      style={FAMILY_TREE_PARCHMENT_SURFACE_STYLE}
                      data-family-tree-react-flow-host="fan"
                    >
                      <FanChart
                        individuals={people}
                        relationships={relationships}
                        rootPersonId={selectedPerson}
                        onPersonClick={(id) => navigateToPersonById(id)}
                      />
                    </div>
                  )}
                  
                  {/* Descendant Chart */}
                  {chartLayout === 'descendants' && (
                    <div
                      className="tree-react-flow-host family-tree-canvas absolute inset-0 min-h-0 min-w-0"
                      style={FAMILY_TREE_PARCHMENT_SURFACE_STYLE}
                      data-family-tree-react-flow-host="descendants"
                    >
                      <DescendantChart
                        individuals={people}
                        relationships={relationships}
                        rootPersonId={selectedPerson}
                        onPersonClick={(id) => navigateToPersonById(id)}
                      />
                    </div>
                  )}

                  {chartLayout === 'tree' &&
                    treeGenerated &&
                    !generatingTree &&
                    nodes.length > 0 && (
                      <ChartWorkspaceHintPanel
                        variant="tree"
                        canAddMembers={tree?.role === 'owner' || tree?.role === 'editor'}
                        isMobile={isMobile}
                        collapsed={chartTipsCollapsed}
                        onCollapse={collapseChartTips}
                        onExpand={expandChartTips}
                      />
                    )}
                  {chartLayout !== 'tree' && selectedPerson && (
                    <ChartWorkspaceHintPanel
                      variant="chart"
                      collapsed={chartTipsCollapsed}
                      onCollapse={collapseChartTips}
                      onExpand={expandChartTips}
                    />
                  )}
                </div>
              </>
            )}
          </TabsContent>

          {/* Timeline Tab */}
          <TabsContent
            value="timeline"
            className="mt-0 outline-none focus-visible:outline-none"
            data-testid="timeline-view"
          >
            <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
              {timelineLoading ? (
                <div className="flex flex-col items-center justify-center py-12">
                  <Loader2 className="h-10 w-10 animate-spin text-primary/50 mb-4" />
                  <p className="text-muted-foreground font-serif">Loading your family's story through time...</p>
                </div>
              ) : !timelineData?.has_dates ? (
                // Empty state
                <div className="text-center py-12">
                  <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-primary/5 flex items-center justify-center">
                    <Clock className="h-10 w-10 text-primary/40" />
                  </div>
                  <h2 className="text-xl font-serif font-semibold mb-3">Your Timeline Awaits</h2>
                  <p className="text-muted-foreground max-w-md mx-auto leading-relaxed">
                    As dates are added to your family members, your family&apos;s timeline will begin to take shape,
                    showing how generations overlap and stories connect through time.
                  </p>
                </div>
              ) : (
                <div className="space-y-6">
                  {/* Timeline Header */}
                  <div className="text-center mb-6">
                    <h2 className="text-2xl font-serif font-semibold mb-2">Your Family Through Time</h2>
                    <p className="text-muted-foreground">
                      {timelineData.earliest_year} — {timelineData.latest_year || 'Present'}
                    </p>
                  </div>

                  {/* Lifespan Visualization */}
                  {timelineData.lifespans.filter(l => l.birth_year).length > 0 && (
                    <div className="mb-8">
                      <h3 className="text-sm font-medium text-muted-foreground mb-4 text-center">Overlapping Lives</h3>
                      <div className="overflow-x-auto pb-2 -mx-1 px-1">
                        <div className="relative min-w-[640px] w-full">
                        {/* Year markers */}
                        <div className="flex justify-between text-xs text-muted-foreground/60 mb-3 px-2">
                          <span>{timelineData.earliest_year}</span>
                          <span>{timelineData.latest_year || new Date().getFullYear()}</span>
                        </div>
                        
                        {/* Lifespan bars */}
                        <div className="space-y-2">
                          {timelineData.lifespans
                            .filter(person => person.birth_year)
                            .sort((a, b) => (a.birth_year || 0) - (b.birth_year || 0))
                            .map((person, index) => {
                              const minYear = timelineData.earliest_year || 1900;
                              const maxYear = timelineData.latest_year || new Date().getFullYear();
                              const totalYears = maxYear - minYear || 1;
                              
                              const birthYear = person.birth_year || minYear;
                              const endYear = person.death_year || new Date().getFullYear();
                              
                              const startPercent = ((birthYear - minYear) / totalYears) * 100;
                              const widthPercent = ((endYear - birthYear) / totalYears) * 100;
                              
                              return (
                                <div 
                                  key={person.id}
                                  className="relative h-9 group cursor-pointer"
                                  onClick={() => navigate(personHref(person))}
                                  data-testid={`lifespan-${person.id}`}
                                  style={{
                                    opacity: 0,
                                    animation: `fadeSlideIn 0.5s ease-out ${index * 0.08}s forwards`
                                  }}
                                >
                                  {/* The lifespan bar - soft, rounded, gradient edge */}
                                  <div
                                    className="absolute h-full rounded-full group-hover:shadow-md"
                                    style={{
                                      left: `${startPercent}%`,
                                      width: `${Math.max(widthPercent, 3)}%`,
                                      background: person.is_living 
                                        ? 'linear-gradient(90deg, hsl(var(--primary) / 0.65) 0%, hsl(var(--primary) / 0.85) 100%)'
                                        : 'linear-gradient(90deg, hsl(var(--primary) / 0.35) 0%, hsl(var(--primary) / 0.5) 100%)',
                                      transition: 'all 0.3s ease-out',
                                    }}
                                  >
                                    {/* Person name inside bar */}
                                    <div className="absolute inset-0 flex items-center px-3 overflow-hidden">
                                      <span className="text-xs font-medium text-primary-foreground truncate whitespace-nowrap drop-shadow-sm">
                                        {person.name}
                                      </span>
                                    </div>
                                  </div>
                                  
                                  {/* Hover tooltip */}
                                  <div className="absolute left-1/2 -translate-x-1/2 -top-10 bg-popover border shadow-lg rounded-lg px-3 py-1.5 text-xs opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-10">
                                    <span className="font-medium">{person.name}</span>
                                    <span className="text-muted-foreground ml-2">
                                      {person.birth_year}{person.death_year ? ` – ${person.death_year}` : ' – present'}
                                    </span>
                                  </div>
                                </div>
                              );
                            })}
                        </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Events by Decade */}
                  <div className="relative">
                    {/* Vertical timeline line - softer */}
                    <div className="absolute left-6 top-0 bottom-0 w-px bg-gradient-to-b from-border via-border/60 to-transparent" />
                    
                    {timelineData.decades.map((decade, decadeIndex) => {
                      const decadeEvents = timelineData.events.filter(e => e.decade === decade);
                      if (decadeEvents.length === 0) return null;
                      
                      return (
                        <div 
                          key={decade} 
                          className="mb-8 last:mb-0" 
                          data-testid={`decade-${decade}`}
                          style={{
                            opacity: 0,
                            animation: `fadeSlideIn 0.4s ease-out ${decadeIndex * 0.1}s forwards`
                          }}
                        >
                          {/* Decade label */}
                          <div className="flex items-center mb-6">
                            <div className="w-12 h-12 rounded-full bg-secondary/80 flex items-center justify-center font-serif font-semibold text-sm z-10 shadow-sm">
                              {decade}
                            </div>
                          </div>
                          
                          {/* Events in this decade */}
                          <div className="ml-16 space-y-4">
                            {decadeEvents.map((event, eventIndex) => {
                              const isMarker = event.event_type === 'marker';
                              const markerStyle = isMarker ? getMarkerTypeStyle(event.marker_type) : null;
                              const MarkerIcon = isMarker ? getMarkerTypeIcon(event.marker_type) : null;
                              return (
                              <div 
                                key={event.id}
                                className="group cursor-pointer"
                                onClick={() => navigateToTimelineEvent(event)}
                                data-testid={`event-${event.id}`}
                                style={{
                                  opacity: 0,
                                  animation: `fadeSlideIn 0.35s ease-out ${(decadeIndex * 0.1) + (eventIndex * 0.05)}s forwards`
                                }}
                              >
                                <div className="relative pl-6 pb-4">
                                  {/* Event dot - softer colors */}
                                  <div 
                                    className={`absolute -left-[2.125rem] top-1.5 w-3 h-3 rounded-full border-2 border-background z-10 transition-transform duration-200 group-hover:scale-125 ${
                                      event.event_type === 'birth' 
                                        ? 'bg-emerald-400' 
                                        : event.event_type === 'death' 
                                        ? 'bg-slate-300'
                                        : event.event_type === 'memory'
                                        ? 'bg-amber-400'
                                        : isMarker && markerStyle
                                        ? markerStyle.dot
                                        : 'bg-rose-300'
                                    }`}
                                  />
                                  
                                  {/* Event card - refined hover */}
                                  <div className="bg-card border border-border/40 rounded-2xl p-5 hover:shadow-lg hover:border-border/60 transition-all duration-300 ease-out">
                                    <div className="flex items-start gap-4">
                                      {event.person_photo ? (
                                        <div className="h-14 w-14 shrink-0 overflow-hidden rounded-xl border border-border/40 bg-muted">
                                          <img
                                            src={resolveAssetUrl(BACKEND_URL, event.person_photo)}
                                            alt=""
                                            loading="lazy"
                                            decoding="async"
                                            className="h-full w-full object-cover"
                                          />
                                        </div>
                                      ) : null}
                                      {/* Event type icon - softer backgrounds */}
                                      <div className={`p-2 rounded-xl shrink-0 transition-colors duration-200 ${
                                        event.event_type === 'birth'
                                          ? 'bg-emerald-50 group-hover:bg-emerald-100/80'
                                          : event.event_type === 'death'
                                          ? 'bg-slate-50 group-hover:bg-slate-100/80'
                                          : event.event_type === 'memory'
                                          ? 'bg-amber-50 group-hover:bg-amber-100/80'
                                          : isMarker && markerStyle
                                          ? markerStyle.iconBg
                                          : 'bg-rose-50 group-hover:bg-rose-100/80'
                                      }`}>
                                        {event.event_type === 'partnership' ? (
                                          <Heart className="h-5 w-5 text-rose-400" />
                                        ) : event.event_type === 'memory' ? (
                                          <Mic className="h-5 w-5 text-amber-600" />
                                        ) : isMarker && MarkerIcon && markerStyle ? (
                                          <MarkerIcon className={`h-5 w-5 ${markerStyle.iconText}`} />
                                        ) : (
                                          <Clock className={`h-5 w-5 ${
                                            event.event_type === 'birth' 
                                              ? 'text-emerald-500' 
                                              : 'text-slate-400'
                                          }`} />
                                        )}
                                      </div>
                                      
                                      <div className="flex-1 min-w-0">
                                        {/* Event label */}
                                        <p className="font-serif text-base mb-1 group-hover:text-primary transition-colors duration-200">
                                          {isMarker && event.year ? (
                                            <span className="tabular-nums text-muted-foreground font-sans text-sm mr-2">{event.year}</span>
                                          ) : null}
                                          {event.event_label}
                                        </p>
                                        
                                        {/* Date and location */}
                                        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-muted-foreground/80">
                                          {!isMarker ? <span>{event.date}</span> : null}
                                          {event.location && (
                                            <span className="flex items-center gap-1">
                                              <MapPin className="h-3 w-3" />
                                              {event.location}
                                            </span>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </TabsContent>

          {/* People, places & things */}
          <TabsContent
            value="memories"
            className="mt-0 outline-none focus-visible:outline-none"
            data-testid="memories-view"
          >
            {tree && (
              <MemoryMarkersWorkspace
                treeId={tree.id}
                people={people}
                canEdit={canEditTree}
                onMarkersChange={handleMemoryMarkersChanged}
              />
            )}
          </TabsContent>

          {/* Audiobook Tab */}
          <TabsContent
            value="audiobook"
            className="mt-0 outline-none focus-visible:outline-none"
            data-testid="audiobook-view"
          >
            <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
              {tree && (
                <AudiobookPlayer treeId={tree.id} canEdit={canEditTree} />
              )}
            </div>
          </TabsContent>

          {/* Keepsake readiness — nav-only tab (left rail + preview modal) */}
          <TabsContent
            value="readiness"
            className="mt-0 outline-none focus-visible:outline-none"
            data-testid="readiness-view"
          >
            <div className="mx-auto w-full max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
              {tree && CORE_JOURNEY_UI ? (
                <CoreLegacyBookReadiness treeId={tree.id} />
              ) : null}
              {tree && (
                <BookProgressPanel
                  treeId={tree.id}
                  tree={tree}
                  onExportPdf={handleExportPdf}
                  onExportPdfV2={LEGACY_BOOK_PLAYWRIGHT_EXPORT ? handleExportPdfV2 : undefined}
                  exportingPdfV2={exportingPdfV2}
                  playwrightExportPhase={playwrightExportPhase}
                  compact={CORE_JOURNEY_UI}
                />
              )}
            </div>
          </TabsContent>

          {/* Archive Settings */}
          <TabsContent
            value="settings"
            className="mt-0 outline-none focus-visible:outline-none"
            data-testid="settings-view"
          >
            {tree ? (
              <ArchiveSettingsPanel
                tree={tree}
                user={user}
                people={people}
                canEditTree={canEditTree}
                isOnline={isOnline}
                peopleCount={people.length}
                visibility={visibility}
                visibilityLoading={visibilityLoading}
                indexedSurnames={indexedSurnames}
                onVisibilityToggle={handleVisibilityToggle}
                onTreeUpdated={handleTreeSettingsUpdated}
                onExportPdf={handleExportPdf}
                onExportJson={handleExportJson}
                exportingPdf={exportingPdf}
                exportingJson={exportingJson}
                canExportArchive={canExportArchive}
                onDeleteArchive={handleDeleteArchive}
                deletingArchive={deletingArchive}
              />
            ) : null}
          </TabsContent>
          </div>
        </Tabs>
        </div>
      </div>

      {/* Add Person Dialog */}
      <Dialog open={showAddDialog} onOpenChange={(open) => {
        setShowAddDialog(open);
        if (!open) {
          setAddStep(1);
          setAddPersonLinkContext(null);
          setNewPerson(blankNewPersonFields);
        }
      }}>
        <DialogContent
          className="flex max-h-[min(90vh,720px)] max-w-lg flex-col gap-0 overflow-hidden p-0 sm:max-w-lg"
          data-testid="add-person-dialog"
        >
          <DialogHeader className="shrink-0 space-y-1.5 px-6 pb-2 pt-6 text-left">
            <DialogTitle>
              {addPersonLinkContext
                ? 'Add family connection to this tree'
                : creatingProfileType === PROFILE_TYPE.PET
                  ? 'Add a family pet'
                  : creatingProfileType === PROFILE_TYPE.COMPANION
                    ? 'Add chosen family or close friend'
                    : 'Add a family connection to your archive'}
            </DialogTitle>
            <DialogDescription>
              {addPersonLinkContext ? (
                <>
                  {addPersonLinkContext.relation === 'parent' && (
                    <>Adding a <strong>parent</strong> of {addPersonLinkContext.anchorName || 'this person'}.</>
                  )}
                  {addPersonLinkContext.relation === 'child' && (
                    <>Adding a <strong>child</strong> of {addPersonLinkContext.anchorName || 'this person'}.</>
                  )}
                  {addPersonLinkContext.relation === 'spouse' && (
                    <>Adding a <strong>partner</strong> linked to {addPersonLinkContext.anchorName || 'this person'}.</>
                  )}{' '}
                  They will be linked on the family tree when you finish.
                </>
              ) : !usesTwoStepAddFlow ? (
                'Give them a name and save. New people start in your private archive until they join or claim their profile.'
              ) : addStep === 1 ? (
                'People join the genealogical tree. Pets and chosen family stay in your archive list only.'
              ) : (
                'Add dates and places when you have them. Everything here is optional.'
              )}
            </DialogDescription>
          </DialogHeader>

          {usageSnapshot && shouldShowPeopleGrowthPrompt(usageSnapshot) ? (
            <div className="shrink-0 px-6 pb-2">
              <SoftUpgradePrompt
                promptId="people-growth"
                treeId={tree?.id}
                visible={showAddDialog}
                snapshot={usageSnapshot}
                message="You're growing beyond the free starter archive. Nothing is blocked today, but the Legacy Book plan will be the best fit when you're ready to create the finished keepsake."
                ctaLabel="View Legacy Book offer"
                ctaTo="/pricing#legacy-book"
                featureName="legacy_book"
                data-testid="soft-upgrade-prompt-add-person"
              />
            </div>
          ) : null}

          <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-6 py-2">
          {addStep === 1 ? (
            <div className="space-y-4 pb-2">
              {!addPersonLinkContext ? (
                <div className="space-y-2">
                  <Label>Choose what you&apos;re adding</Label>
                  <p className="text-xs leading-relaxed text-muted-foreground">
                    <strong>Person</strong> — added to your private archive on the genealogical tree until they join.{' '}
                    <strong>Family pet</strong> or <strong>chosen family / friend</strong> — saved as archive
                    connections only (link tree relationships from their profile if needed).
                  </p>
                  <div className="grid grid-cols-1 gap-2 sm:grid-cols-3" data-testid="profile-type-select">
                    {[
                      { value: PROFILE_TYPE.PERSON, label: 'Person' },
                      { value: PROFILE_TYPE.PET, label: 'Family pet' },
                      { value: PROFILE_TYPE.COMPANION, label: 'Chosen family / friend' },
                    ].map((opt) => (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() =>
                          setNewPerson({
                            ...blankNewPersonFields,
                            profile_type: opt.value,
                            given_names: newPerson.given_names,
                            surname: opt.value === PROFILE_TYPE.PERSON ? newPerson.surname : '',
                          })
                        }
                        className={treeProfileTypePickClass(creatingProfileType === opt.value)}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>
              ) : null}
              {(addPersonLinkContext || creatingProfileType === PROFILE_TYPE.PERSON) ? (
                <ArchiveConnectionPrivacyNotice compact />
              ) : null}
              <div>
                <Label htmlFor="given-names">
                  {creatingProfileType === PROFILE_TYPE.PET ? 'Name *' : 'First name(s) *'}
                </Label>
                <Input
                  id="given-names"
                  placeholder={creatingProfileType === PROFILE_TYPE.PET ? 'Barnaby' : 'John William'}
                  value={newPerson.given_names}
                  onChange={(e) => setNewPerson({...newPerson, given_names: e.target.value})}
                  data-testid="given-names-input"
                  autoFocus
                />
              </div>
              {creatingProfileType === PROFILE_TYPE.PERSON ? (
                <div>
                  <Label htmlFor="surname">Last name *</Label>
                  <Input
                    id="surname"
                    placeholder="Smith"
                    value={newPerson.surname}
                    onChange={(e) => setNewPerson({...newPerson, surname: e.target.value})}
                    data-testid="surname-input"
                  />
                </div>
              ) : null}
              {!addPersonLinkContext && creatingProfileType === PROFILE_TYPE.PERSON ? (
                <ProfileIntentionPicker
                  value={newPerson.profile_intention || PROFILE_INTENTION.STANDARD}
                  onChange={(profile_intention) => setNewPerson({ ...newPerson, profile_intention })}
                  compact
                />
              ) : null}
              {creatingProfileType === PROFILE_TYPE.PET ? (
                <>
                  <div>
                    <Label htmlFor="species">Species</Label>
                    <Select
                      value={newPerson.species ?? undefined}
                      onValueChange={(value) => setNewPerson({ ...newPerson, species: value })}
                    >
                      <SelectTrigger data-testid="species-select">
                        <SelectValue placeholder="Select species (optional)" />
                      </SelectTrigger>
                      <SelectContent>
                        {PET_SPECIES.map((s) => (
                          <SelectItem key={s.value} value={s.value}>
                            {s.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="breed">Breed (optional)</Label>
                    <Input
                      id="breed"
                      placeholder="Cocker spaniel"
                      value={newPerson.breed || ''}
                      onChange={(e) => setNewPerson({ ...newPerson, breed: e.target.value })}
                      data-testid="breed-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="attached-person">Part of someone&apos;s story (optional)</Label>
                    <Select
                      value={newPerson.attached_to_person_id || '__none__'}
                      onValueChange={(value) =>
                        setNewPerson({
                          ...newPerson,
                          attached_to_person_id: value === '__none__' ? null : value,
                        })
                      }
                    >
                      <SelectTrigger data-testid="pet-attached-person-select">
                        <SelectValue placeholder="Link to a person in the tree" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">Not linked</SelectItem>
                        {genealogicalPeopleForAttachment.map((p) => (
                          <SelectItem key={p.id} value={p.id}>
                            {getDisplayName(p)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </>
              ) : null}
              {creatingProfileType === PROFILE_TYPE.COMPANION ? (
                <>
                  <div>
                    <Label htmlFor="companion-type">Connection to your family *</Label>
                    <Select
                      value={newPerson.companion_type ?? undefined}
                      onValueChange={(value) => setNewPerson({ ...newPerson, companion_type: value })}
                    >
                      <SelectTrigger data-testid="companion-type-select">
                        <SelectValue placeholder="How are they connected?" />
                      </SelectTrigger>
                      <SelectContent>
                        {COMPANION_TYPE_OPTIONS.map((opt) => (
                          <SelectItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="family-connection">Connection to the family (optional)</Label>
                    <Input
                      id="family-connection"
                      placeholder="Like an aunt to our children"
                      value={newPerson.family_connection || ''}
                      onChange={(e) => setNewPerson({ ...newPerson, family_connection: e.target.value })}
                      data-testid="family-connection-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="companion-surname">Last name (optional)</Label>
                    <Input
                      id="companion-surname"
                      placeholder="Reeves"
                      value={newPerson.surname || ''}
                      onChange={(e) => setNewPerson({ ...newPerson, surname: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label htmlFor="companion-attached-person">Part of someone&apos;s story (optional)</Label>
                    <Select
                      value={newPerson.attached_to_person_id || '__none__'}
                      onValueChange={(value) =>
                        setNewPerson({
                          ...newPerson,
                          attached_to_person_id: value === '__none__' ? null : value,
                        })
                      }
                    >
                      <SelectTrigger data-testid="companion-attached-person-select">
                        <SelectValue placeholder="Link to a person in the tree" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">Not linked</SelectItem>
                        {genealogicalPeopleForAttachment.map((p) => (
                          <SelectItem key={p.id} value={p.id}>
                            {getDisplayName(p)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </>
              ) : null}
              {creatingProfileType === PROFILE_TYPE.PERSON ? (
                <div>
                  <Label htmlFor="sex">Gender</Label>
                  <Select
                    value={newPerson.sex ?? undefined}
                    onValueChange={(value) => setNewPerson({ ...newPerson, sex: value })}
                  >
                    <SelectTrigger data-testid="sex-select">
                      <SelectValue placeholder="Select gender (optional)" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="male">Male</SelectItem>
                      <SelectItem value="female">Female</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              ) : null}
              {!usesTwoStepAddFlow ? (
                <details className="rounded-lg border border-border/45 bg-muted/10">
                  <summary className="cursor-pointer list-none px-3 py-2.5 text-sm font-medium text-foreground [&::-webkit-details-marker]:hidden">
                    Optional details
                  </summary>
                  <div className="space-y-4 border-t border-border/35 px-3 pb-4 pt-4">
                    <SmartDateInput
                      mode="simple"
                      placeholder="Year"
                      label={getBirthDateLabel(newPerson)}
                      value={newPerson.birth_date}
                      onChange={(val) => setNewPerson({ ...newPerson, birth_date: val })}
                      data-testid="birth-date-input"
                    />
                    <div className="space-y-2">
                      <Label htmlFor="birth-place">Location (optional)</Label>
                      <Input
                        id="birth-place"
                        placeholder="Town, county, or country"
                        value={newPerson.birth_place}
                        onChange={(e) => setNewPerson({ ...newPerson, birth_place: e.target.value })}
                        data-testid="birth-place-input"
                        autoComplete="off"
                      />
                    </div>
                    <SmartDateInput
                      mode="simple"
                      placeholder="Year"
                      label={getDeathDateLabel(newPerson)}
                      value={newPerson.death_date}
                      onChange={(val) => setNewPerson({ ...newPerson, death_date: val })}
                      data-testid="death-date-input"
                    />
                    <div className="space-y-2">
                      <Label htmlFor="biographical-notes">{getBiographicalNotesLabel(newPerson)}</Label>
                      <textarea
                        id="biographical-notes"
                        className="flex min-h-[72px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        placeholder={
                          creatingProfileType === PROFILE_TYPE.PET
                            ? 'Gentle, faithful friend through every season…'
                            : 'What stays with the family when you think of them…'
                        }
                        value={newPerson.biographical_notes || ''}
                        onChange={(e) => setNewPerson({ ...newPerson, biographical_notes: e.target.value })}
                        data-testid="biographical-notes-input"
                      />
                    </div>
                  </div>
                </details>
              ) : null}
            </div>
          ) : (
            <div className="space-y-4 pb-2">
              <p className="text-xs leading-relaxed text-muted-foreground">
                Dates and places help family find this profile later. All optional.
              </p>
              <div className="space-y-4">
                <SmartDateInput
                  label={getBirthDateLabel(newPerson)}
                  value={newPerson.birth_date}
                  onChange={(val) => setNewPerson({ ...newPerson, birth_date: val })}
                  collapseAdvanced
                  data-testid="birth-date-input"
                />
                <div className="space-y-2">
                  <Label htmlFor="birth-place">Birth place (optional)</Label>
                  {recentPlacesForPicker.length > 0 && (
                    <div className="flex flex-wrap gap-1.5" data-testid="recent-place-chips">
                      <span className="w-full text-[11px] text-muted-foreground">Recent in this browser:</span>
                      {recentPlacesForPicker.map((p) => (
                        <button
                          key={`b-${p}`}
                          type="button"
                          className="rounded-full border border-border/60 bg-muted/25 px-2.5 py-1 text-xs text-foreground hover:border-primary/40 hover:bg-muted/50"
                          onClick={() => setNewPerson({ ...newPerson, birth_place: p })}
                        >
                          {p}
                        </button>
                      ))}
                    </div>
                  )}
                  <Input
                    id="birth-place"
                    placeholder={birthOrDeathPlacePlaceholder()}
                    value={newPerson.birth_place}
                    onChange={(e) => setNewPerson({ ...newPerson, birth_place: e.target.value })}
                    data-testid="birth-place-input"
                    autoComplete="off"
                  />
                </div>
              </div>

              <details className="rounded-lg border border-border/45 bg-muted/10">
                <summary className="cursor-pointer list-none px-3 py-2.5 text-sm font-medium text-foreground [&::-webkit-details-marker]:hidden">
                  Death (optional)
                </summary>
                <div className="space-y-4 border-t border-border/35 px-3 pb-4 pt-4">
                  <SmartDateInput
                    label={getDeathDateLabel(newPerson)}
                    value={newPerson.death_date}
                    onChange={(val) => setNewPerson({ ...newPerson, death_date: val })}
                    showDeathContext
                    collapseAdvanced
                    data-testid="death-date-input"
                  />
                  <div className="space-y-2">
                    <Label htmlFor="death-place">Death place (optional)</Label>
                    {recentPlacesForPicker.length > 0 && (
                      <div className="flex flex-wrap gap-1.5">
                        <span className="w-full text-[11px] text-muted-foreground">Recent:</span>
                        {recentPlacesForPicker.map((p) => (
                          <button
                            key={`d-${p}`}
                            type="button"
                            className="rounded-full border border-border/60 bg-muted/25 px-2.5 py-1 text-xs text-foreground hover:border-primary/40 hover:bg-muted/50"
                            onClick={() => setNewPerson({ ...newPerson, death_place: p })}
                          >
                            {p}
                          </button>
                        ))}
                      </div>
                    )}
                    <Input
                      id="death-place"
                      placeholder={birthOrDeathPlacePlaceholder()}
                      value={newPerson.death_place}
                      onChange={(e) => setNewPerson({ ...newPerson, death_place: e.target.value })}
                      data-testid="death-place-input"
                      autoComplete="off"
                    />
                  </div>
                </div>
              </details>
            </div>
          )}
          </div>

          <DialogFooter className="shrink-0 gap-2 border-t bg-background px-6 py-4 sm:flex-row sm:justify-end">
            {usesTwoStepAddFlow ? (
              addStep === 1 ? (
                <>
                  <Button variant="outline" onClick={() => setShowAddDialog(false)}>
                    Cancel
                  </Button>
                  <Button onClick={() => setAddStep(2)} data-testid="next-step-btn">
                    Continue
                  </Button>
                </>
              ) : (
                <>
                  <Button variant="outline" onClick={() => setAddStep(1)}>
                    Back
                  </Button>
                  <Button onClick={handleAddPerson} data-testid="finish-add-btn">
                    Add to family tree
                  </Button>
                </>
              )
            ) : (
              <>
                <Button variant="outline" onClick={() => setShowAddDialog(false)}>
                  Cancel
                </Button>
                <Button onClick={handleAddPerson} data-testid="finish-add-btn">
                  Add to archive
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Global Search Modal */}
      <Dialog open={searchOpen} onOpenChange={(open) => !open && handleSearchClose()}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="search-dialog">
          <DialogHeader className="pb-0">
            <DialogTitle className="font-serif text-xl">Search Your Family History</DialogTitle>
            <DialogDescription>
              Find people, places, or stories across your family tree
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex-1 flex flex-col min-h-0 pt-4">
            {/* Search Input */}
            <div className="relative mb-4">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
              <Input
                placeholder="Search for names, places, or story text..."
                value={globalSearchQuery}
                onChange={(e) => setGlobalSearchQuery(e.target.value)}
                className="pl-12 h-12 text-base rounded-xl"
                autoFocus
                data-testid="global-search-input"
              />
              {searchLoading && (
                <Loader2 className="absolute right-4 top-1/2 -translate-y-1/2 h-5 w-5 animate-spin text-muted-foreground" />
              )}
            </div>
            
            {/* Chapter Filter */}
            {chaptersAvailable.length > 0 && (
              <div className="flex gap-2 mb-4 flex-wrap">
                <Button
                  variant={searchChapterFilter === '' ? 'secondary' : 'ghost'}
                  size="sm"
                  onClick={() => setSearchChapterFilter('')}
                  className="rounded-full text-xs"
                >
                  All Stories
                </Button>
                {chaptersAvailable.map(ch => (
                  <Button
                    key={ch.id}
                    variant={searchChapterFilter === ch.id ? 'secondary' : 'ghost'}
                    size="sm"
                    onClick={() => setSearchChapterFilter(ch.id)}
                    className="rounded-full text-xs"
                  >
                    {ch.title}
                  </Button>
                ))}
              </div>
            )}
            
            {/* Results */}
            <div className="flex-1 overflow-y-auto space-y-6 pr-2">
              {!searchResults ? (
                <div className="text-center py-12 text-muted-foreground">
                  <Search className="h-12 w-12 mx-auto mb-4 opacity-30" />
                  <p>Start typing to search people, stories, and connected memories</p>
                </div>
              ) : searchResults.total_results === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <Search className="h-12 w-12 mx-auto mb-4 opacity-30" />
                  <p className="font-medium mb-1">No matches found</p>
                  <p className="text-sm">Try a different spelling or fewer words</p>
                </div>
              ) : (
                <>
                  {/* People Results */}
                  {searchResults.people.length > 0 && (
                    <div>
                      <h3 className="text-sm font-medium text-muted-foreground mb-3">
                        People ({searchResults.people.length})
                      </h3>
                      <div className="space-y-2">
                        {searchResults.people.map(person => (
                          <div
                            key={person.id}
                            className="flex items-center gap-3 p-3 rounded-xl border border-border/50 hover:bg-muted/50 cursor-pointer transition-colors"
                            onClick={() => { handleSearchClose(); navigate(personHref(person)); }}
                            data-testid={`search-result-person-${person.id}`}
                          >
                            <PersonAvatarWithIndicators
                              photoUrl={person.photo ? resolveAssetUrl(BACKEND_URL, person.photo) : null}
                              initials={person.name.split(' ').map(n => n[0]).join('').slice(0, 2)}
                              size="sm"
                              storyCount={storyCounts[person.id] ?? 0}
                              documentCount={documentCounts[person.id] ?? 0}
                            />
                            <div className="flex-1 min-w-0">
                              <p className="font-medium truncate">{person.name}</p>
                              <p className="text-xs text-muted-foreground truncate">
                                {person.match_context}
                                {person.birth_date && ` • ${formatDisplayDate(person.birth_date)}`}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Story Results */}
                  {searchResults.stories.length > 0 && (
                    <div>
                      <h3 className="text-sm font-medium text-muted-foreground mb-3">
                        Stories ({searchResults.stories.length})
                      </h3>
                      <div className="space-y-2">
                        {searchResults.stories.map(story => (
                          <div
                            key={story.id}
                            className="p-4 rounded-xl border border-border/50 hover:bg-muted/50 cursor-pointer transition-colors"
                            onClick={() => { handleSearchClose(); navigateToPersonById(story.person_id); }}
                            data-testid={`search-result-story-${story.id}`}
                          >
                            <div className="flex items-start gap-3 mb-2">
                              <div className="w-8 h-8 rounded-full bg-secondary/50 flex items-center justify-center flex-shrink-0 overflow-hidden">
                                {story.person_photo ? (
                                  <img src={resolveAssetUrl(BACKEND_URL, story.person_photo)} alt="" className="w-full h-full object-cover" />
                                ) : (
                                  <span className="text-xs font-serif text-primary/70">
                                    {story.person_name.split(' ').map(n => n[0]).join('').slice(0, 2)}
                                  </span>
                                )}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="font-medium text-sm">{story.person_name}</p>
                                {story.chapter_title && (
                                  <span className="text-xs text-muted-foreground">{story.chapter_title}</span>
                                )}
                              </div>
                            </div>
                            <p className="text-sm text-muted-foreground italic mb-1 line-clamp-1">
                              "{story.question_text}"
                            </p>
                            <p className="text-sm line-clamp-2">{story.snippet}</p>
                            <p className="text-xs text-muted-foreground/70 mt-1">
                              Matched in: {story.match_context}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {(searchResults.memory_markers || []).length > 0 && (
                    <div>
                      <h3 className="text-sm font-medium text-muted-foreground mb-3">
                        People, places &amp; things ({searchResults.memory_markers.length})
                      </h3>
                      <div className="space-y-2">
                        {searchResults.memory_markers.map((marker) => {
                          const Icon = getMarkerTypeIcon(marker.marker_type);
                          const style = getMarkerTypeStyle(marker.marker_type);
                          const img = marker.image_url ? resolveAssetUrl(BACKEND_URL, marker.image_url) : null;
                          return (
                            <div
                              key={marker.id}
                              className="flex items-start gap-3 p-3 rounded-xl border border-border/50 hover:bg-muted/50 cursor-pointer transition-colors"
                              onClick={() => navigateToMarkerSearchResult(marker)}
                              data-testid={`search-result-marker-${marker.id}`}
                            >
                              {img ? (
                                <div className="h-12 w-12 shrink-0 overflow-hidden rounded-lg border bg-muted">
                                  <img src={img} alt="" className="h-full w-full object-cover" />
                                </div>
                              ) : (
                                <div className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-lg ${style.iconBg}`}>
                                  <Icon className={`h-5 w-5 ${style.iconText}`} aria-hidden />
                                </div>
                              )}
                              <div className="min-w-0 flex-1">
                                <div className="flex flex-wrap items-center gap-2">
                                  <p className="font-medium truncate">{marker.title}</p>
                                  <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
                                    {MARKER_TYPE_LABELS[marker.marker_type] || marker.marker_type}
                                  </span>
                                </div>
                                {marker.snippet ? (
                                  <p className="text-sm text-muted-foreground line-clamp-2 mt-0.5">{marker.snippet}</p>
                                ) : null}
                                <p className="text-xs text-muted-foreground/70 mt-1">
                                  {marker.match_context}
                                  {marker.approx_date ? ` · ${marker.approx_date}` : ''}
                                  {marker.person_name ? ` · ${marker.person_name}` : ''}
                                </p>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
            
            {/* Offline indicator */}
            {!isOnline && (
              <div className="mt-4 pt-3 border-t flex items-center gap-2 text-sm text-amber-600">
                <WifiOff className="h-4 w-4" />
                <span>Searching offline cache — stories unavailable</span>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TreeViewEnhanced;